import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../../config/firebase';
import { collection, query, where, orderBy, limit, onSnapshot, getDocs, getDoc, doc, updateDoc, addDoc, increment, serverTimestamp, startAfter, documentId } from 'firebase/firestore';
import { authService } from '../../services/authService';
import BarcodeScanner from '../BarcodeScanner';
import { imprimirOrden } from '../../utils/printDocument';
import WeeklyAgenda from '../WeeklyAgenda';
import OrdenesAnalitica from './OrdenesAnalitica';
import IncidenciasTab from './IncidenciasTab';
import RouteClosuresList from '../RouteClosuresList';
import CommissionsList from '../CommissionsList';
import Paginacion from '../common/Paginacion';
import { buscarProductosPorTexto } from '../../utils/buscarProductos';

// ── Mapa document-state-id → estado string (esquema raw StelOrder) ─────────────
const STEL_STATE_MAP = {
  651457:  'pendiente',
  1018827: 'en_ruta',
  1016222: 'pendiente_material',
  1016221: 'pendiente_sustitucion',
  1007173: 'pendiente_pago',
  1016219: 'cobrada',
  1004686: 'garantia',
  1016220: 'ausente',
  1040205: 'pendiente_presupuesto',
  1305009: 'instalacion',
  1041994: 'garantia_ariston',
  1069646: 'garantia_nofer',
  1069647: 'garantia_svan',
  1070405: 'garantia_logman',
  1226452: 'garantia_beretta',
  1069430: 'puesta_marcha', // "PM" en WeeklyAgenda.js/TechnicianOrderDetailView.js — antes quedaba con estado null aquí, fuera de "En campo hoy"
  // Faltaban estos dos — ya existían en STEL_ESTADO (updateWorkorderState.js)
  // pero nunca se habían copiado aquí. Sin 651428, una orden raw recién
  // cerrada en StelOrder quedaba con estado null: invisible para cualquier
  // métrica de "cerradas" (detectado al ver "Analítica" con 0 cierres en
  // 14 días siendo evidentemente falso).
  651428: 'cerrada',
  651446: 'rechazada',
};

// Mapa assignee-id → nombre de técnico para órdenes raw de StelOrder
const TECNICO_ID_NOMBRE = { 21936: 'Benito', 23415: 'Pablo', 21945: 'Dani', 31171: 'Ivan', 25901: 'Joaquin' };

// Normaliza en memoria un doc raw de StelOrder al esquema que usa OrdenesView.
// Los docs del esquema viejo (con 'estado' y 'fechaProgramada') se devuelven sin cambios.
function normalizeRaw(o) {
  if (o.estado) return o;
  const tecnicoId = o.tecnicoId || o['assignee-id'] || o['agent-id'] || null;
  return {
    ...o,
    fechaProgramada: o.date ? String(o.date).slice(0, 10) : null,
    estado:          STEL_STATE_MAP[o['document-state-id']] || null,
    referencia:      o.referencia || o['full-reference'] || '',
    tecnicoId,
    tecnicoNombre:   o.tecnicoNombre || (tecnicoId ? (TECNICO_ID_NOMBRE[Number(tecnicoId)] || null) : null),
    clienteNombre:   o.clienteNombre || o['customer-name'] || '',
  };
}

// La misma orden real de StelOrder puede existir como DOS documentos de
// Firestore en paralelo: uno con id numérico plano (el que refresca solo
// cada 30 min el cron syncDeltaStelOrder.js — sin enriquecer, pero siempre
// al día) y otro con id "ord-<id>" (de syncOrdenes.js, aún disparado a mano
// desde la vista de técnico — trae cliente/dirección, pero una vez creado
// nunca se vuelve a tocar, así que su estado puede quedarse desfasado).
// Verificado contra producción el 2026-09-15: en varias órdenes de hoy el
// "ord-" mostraba un estado distinto y más viejo que el numérico real.
// Sin esto, la UI mostraba la misma orden dos veces — una con cliente y
// estado atrasado, otra sin cliente pero con el estado bueno.
function esDocViejo(o) { return typeof o.id === 'string' && o.id.startsWith('ord-'); }
function idNegocio(o) { const id = String(o.id ?? ''); return id.startsWith('ord-') ? id.slice(4) : id; }

// Campos donde el doc numérico (fresco) manda siempre que traiga un valor
// no vacío — son los que de verdad cambian con la sincronización. El resto
// (cliente, dirección, teléfono...) se queda con lo que ya tuviera el "ord-"
// (enriquecido), ya que normalizeRaw() rellena esos campos del numérico con
// '' por defecto — un spread sin criterio los pisaría con ese vacío.
const CAMPOS_AUTORIDAD_FRESCO = ['estado', 'fechaProgramada', 'date', 'document-state-id', 'tecnicoId', 'tecnicoNombre'];

function mergeDuplicados(lista) {
  const porId = new Map();
  for (const o of lista) {
    const key = idNegocio(o);
    const existente = porId.get(key);
    if (!existente) { porId.set(key, o); continue; }
    const [viejo, fresco] = esDocViejo(o) ? [o, existente] : esDocViejo(existente) ? [existente, o] : [existente, o];
    const combinado = { ...viejo };
    for (const campo of CAMPOS_AUTORIDAD_FRESCO) {
      if (fresco[campo] !== undefined && fresco[campo] !== null && fresco[campo] !== '') combinado[campo] = fresco[campo];
    }
    combinado.id = fresco.id; // el id numérico es el que de verdad sigue sincronizándose — cualquier guardado debe ir ahí, no al "ord-" congelado
    porId.set(key, combinado);
  }
  return [...porId.values()];
}

// Las órdenes recién sincronizadas (id numérico) no traen nombre/dirección
// de cliente en el propio documento — solo "account-id". El nombre real
// vive en la colección "clientes", que el mismo cron mantiene al día
// (TABLAS en syncDeltaStelOrder.js incluye 'clients'→'clientes'). Mismo
// patrón de enriquecimiento que ya usa getStelOrderData.js (WeeklyAgenda) —
// aquí en el cliente en vez de en una función, ya que OrdenesView lee
// Firestore directamente. Caché de módulo: mientras dure la sesión de la
// pestaña, un account-id resuelto no se vuelve a leer.
const _clienteCache = new Map();
async function enriquecerConClientes(lista) {
  const faltantes = [...new Set(
    lista.filter(o => !o.clienteNombre && o['account-id'] && !_clienteCache.has(o['account-id']))
         .map(o => o['account-id'])
  )];
  const chunksClientes = [];
  for (let i = 0; i < faltantes.length; i += 30) chunksClientes.push(faltantes.slice(i, i + 30).map(String)); // Firestore 'in' admite hasta 30 valores
  // En paralelo, no uno a uno — con muchos account-id distintos (p. ej. el
  // backlog completo de Analítica) un await secuencial por tanda tarda
  // segundos × número de tandas en vez de lo que tarde la más lenta.
  await Promise.all(chunksClientes.map(async chunk => {
    try {
      const snap = await getDocs(query(collection(db, 'clientes'), where(documentId(), 'in', chunk)));
      snap.docs.forEach(d => _clienteCache.set(Number(d.id), d.data()));
    } catch { /* best-effort — si falla, la orden se queda sin enriquecer, no rompe la lista */ }
  }));
  if (faltantes.length === 0) return lista;
  return lista.map(o => {
    if (o.clienteNombre || !o['account-id']) return o;
    const c = _clienteCache.get(o['account-id']);
    if (!c) return o;
    const mainAddr = c['main-address'] || {};
    const direccionTexto = [mainAddr['address-data'] || mainAddr['formatted-address'], mainAddr['postal-code'], mainAddr['city-town']].filter(Boolean).join(', ');
    return {
      ...o,
      clienteNombre:   c.name || c['legal-name'] || '',
      clienteTelefono: o.clienteTelefono || c.phone || c.phone1 || '',
      direccionTexto:  o.direccionTexto  || direccionTexto      || '',
    };
  });
}

// Un doc "ord-<id>" es una FOTO CONGELADA de cuando se sincronizó por
// última vez (syncOrdenes.js) — si StelOrder reprogramó esa orden a otro
// día DESPUÉS de esa foto, nadie vuelve a tocar el "ord-" nunca más: se
// queda anclado en la fecha/estado que tenía entonces para siempre. Si esa
// entrada no tuvo gemelo numérico en el mismo lote (mergeDuplicados no tuvo
// nada con qué corregirla), puede colarse en "Hoy" o en un rango de fechas
// aunque la orden REAL ya no sea de ese día. Se comprueba cada "ord-" que
// siga suelto contra su documento numérico real (si existe) y se descarta
// o se corrige según lo que diga ese, nunca la foto vieja.
// `esValido(fechaProgramada)` decide si, tras la comprobación, la fecha
// real sigue perteneciendo al rango que se estaba pidiendo.
async function reconciliarViejos(lista, esValido) {
  const viejosSueltos = lista.filter(esDocViejo);
  if (viejosSueltos.length === 0) return lista;
  const ids = [...new Set(viejosSueltos.map(idNegocio))];
  const chunksIds = [];
  for (let i = 0; i < ids.length; i += 30) chunksIds.push(ids.slice(i, i + 30));
  const numericos = new Map();
  // En paralelo — con un backlog grande (cientos de "ord-" sueltos) un await
  // secuencial por tanda de 30 tardaba más de un minuto; en paralelo tarda
  // lo que tarde la tanda más lenta.
  await Promise.all(chunksIds.map(async chunk => {
    try {
      const snap = await getDocs(query(collection(db, 'ordenes'), where(documentId(), 'in', chunk)));
      snap.docs.forEach(d => numericos.set(d.id, normalizeRaw({ id: d.id, ...d.data() })));
    } catch { /* best-effort — sin confirmación, se confía en el "ord-" tal cual */ }
  }));
  return lista
    .filter(o => {
      if (!esDocViejo(o)) return true;
      const real = numericos.get(idNegocio(o));
      return !real || esValido(real.fechaProgramada); // sin gemelo → no hay forma de saber que esté mal, se deja
    })
    .map(o => {
      if (!esDocViejo(o)) return o;
      const real = numericos.get(idNegocio(o));
      if (!real) return o;
      const combinado = { ...o };
      for (const campo of CAMPOS_AUTORIDAD_FRESCO) { if (real[campo] !== undefined && real[campo] !== null && real[campo] !== '') combinado[campo] = real[campo]; }
      combinado.id = real.id;
      return combinado;
    });
}

// "Hoy" en España, no en el huso horario del navegador. new Date().
// toISOString() convierte a UTC — durante la primera hora (o dos, en
// verano) después de medianoche en Madrid, UTC todavía marca el día
// anterior, así que "Hoy" se quedaba corto justo cuando se sincronizaban
// las primeras órdenes de la mañana. Intl.DateTimeFormat con
// timeZone:'Europe/Madrid' da el día correcto sin depender de cómo tenga
// configurado el reloj el ordenador de quien abre la app; 'en-CA' formatea
// directamente como YYYY-MM-DD. Mismo criterio que localDate() en
// syncOrdenes.js/importarStelOrderOrdenes.js del lado servidor.
const FMT_FECHA_ES = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' });
function hoyMadrid() { return FMT_FECHA_ES.format(new Date()); }
// Suma/resta días a una fecha 'YYYY-MM-DD' anclando a mediodía UTC — evita
// que sumar/restar cruce de día por un huso horario de por medio.
function sumarDias(fechaStr, dias) {
  const d = new Date(fechaStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().split('T')[0];
}

// Traduce el filtro de fecha de la pestaña "Todas" a un rango [desde, hasta)
// de strings 'YYYY-MM-DD' para acotar la lectura en Firestore — en vez de
// traer la colección entera y filtrar en cliente. null = sin acotar
// ("Todas las fechas"), la única opción que pagina por cursor.
function rangoFechas(filtro, desde, hasta) {
  const hoy = hoyMadrid();
  if (filtro === 'hoy') {
    return { desde: hoy, hasta: sumarDias(hoy, 1) };
  }
  if (filtro === 'semana') {
    const diaSemana = new Date(hoy + 'T12:00:00Z').getUTCDay(); // 0=domingo…6=sábado
    const lunes = sumarDias(hoy, -((diaSemana + 6) % 7));
    return { desde: lunes, hasta: sumarDias(lunes, 7) };
  }
  if (filtro === 'mes') {
    const [y, m] = hoy.split('-');
    const inicioMesSig = m === '12' ? `${Number(y) + 1}-01-01` : `${y}-${String(Number(m) + 1).padStart(2, '0')}-01`;
    return { desde: `${y}-${m}-01`, hasta: inicioMesSig };
  }
  if (filtro === 'anio') {
    const y = hoy.split('-')[0];
    return { desde: `${y}-01-01`, hasta: `${Number(y) + 1}-01-01` };
  }
  if (filtro === 'rango' && desde && hasta) {
    // `hasta` es el último día incluido para quien elige el rango — se
    // acota con < al día siguiente para no dejarlo fuera del filtro.
    return { desde, hasta: sumarDias(hasta, 1) };
  }
  return null;
}

// ── Constantes ─────────────────────────────────────────────────────────────────

const TECNICOS_FALLBACK = ['Benito', 'Pablo', 'Dani', 'Ivan'];

const TECNICO_COLORS = [
  { dot: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-800'     },
  { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800'},
  { dot: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-800'   },
  { dot: 'bg-red-500',     badge: 'bg-red-100 text-red-800'       },
  { dot: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-800' },
  { dot: 'bg-cyan-500',    badge: 'bg-cyan-100 text-cyan-800'     },
  { dot: 'bg-pink-500',    badge: 'bg-pink-100 text-pink-800'     },
  { dot: 'bg-lime-500',    badge: 'bg-lime-100 text-lime-800'     },
];

// Genera o recupera el color asignado a un técnico (índice estable por nombre)
const _colorCache = {};
function getTecnicoColor(nombre) {
  if (!_colorCache[nombre]) {
    const idx = Object.keys(_colorCache).length % TECNICO_COLORS.length;
    _colorCache[nombre] = TECNICO_COLORS[idx];
  }
  return _colorCache[nombre];
}
// Pre-asignar colores a los técnicos conocidos para estabilidad visual
['Benito','Pablo','Dani','Ivan'].forEach((n, i) => { _colorCache[n] = TECNICO_COLORS[i]; });

const TECNICO_DOT   = new Proxy({}, { get: (_, n) => getTecnicoColor(String(n)).dot   });
const TECNICO_BADGE = new Proxy({}, { get: (_, n) => getTecnicoColor(String(n)).badge });

function useTecnicos() {
  const [tecnicos, setTecnicos] = useState(TECNICOS_FALLBACK);
  useEffect(() => {
    getDocs(collection(db, 'tecnicos'))
      .then(snap => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(t => t.activo !== false)
          .map(t => t.nombre)
          .filter(Boolean)
          .sort();
        if (list.length > 0) {
          list.forEach((n, i) => { if (!_colorCache[n]) _colorCache[n] = TECNICO_COLORS[i % TECNICO_COLORS.length]; });
          setTecnicos(list);
        }
      })
      .catch(() => {});
  }, []);
  return tecnicos;
}

const TIPO_CFG = {
  sat:           { label: 'SAT',         cls: 'bg-orange-100 text-orange-800' },
  instalacion:   { label: 'Instalación', cls: 'bg-blue-100 text-blue-800'    },
  mantenimiento: { label: 'Mant.',       cls: 'bg-green-100 text-green-800'  },
  presupuesto:   { label: 'Ppto.',       cls: 'bg-purple-100 text-purple-800'},
  garantia:      { label: 'Garantía',    cls: 'bg-gray-100 text-gray-700'    },
};

// Estados reales de Firestore (los que guarda finalizarVisita + los estados activos)
const ESTADO_CFG = {
  pendiente:              { label: 'Pendiente',          cls: 'bg-yellow-100 text-yellow-800'  },
  en_ruta:                { label: 'En ruta',            cls: 'bg-blue-100 text-blue-800'      },
  garantia:               { label: 'Garantía activa',    cls: 'bg-indigo-100 text-indigo-800'  },
  instalacion:            { label: 'Instalación activa', cls: 'bg-indigo-100 text-indigo-700'  },
  cobrada:                { label: 'Cobrada',            cls: 'bg-emerald-100 text-emerald-800'},
  cobrada_sin_firma:      { label: 'Cobrada (sin firma)', cls: 'bg-yellow-100 text-yellow-800' },
  pendiente_pago:         { label: 'Pdte. pago',         cls: 'bg-red-100 text-red-800'        },
  pendiente_material:     { label: 'Pdte. material',     cls: 'bg-orange-100 text-orange-800'  },
  pendiente_presupuesto:  { label: 'Pdte. presupuesto',  cls: 'bg-purple-100 text-purple-800'  },
  pendiente_sustitucion:  { label: 'Pdte. sustitución',  cls: 'bg-cyan-100 text-cyan-800'      },
  ausente:                { label: 'Ausente',            cls: 'bg-yellow-100 text-yellow-800'  },
  garantia_ariston:       { label: 'Garantía Ariston',   cls: 'bg-gray-100 text-gray-700'      },
  garantia_nofer:         { label: 'Garantía Nofer',     cls: 'bg-gray-100 text-gray-700'      },
  garantia_svan:          { label: 'Garantía Svan',      cls: 'bg-gray-100 text-gray-700'      },
  garantia_logman:        { label: 'Garantía Logman',    cls: 'bg-gray-100 text-gray-700'      },
  garantia_beretta:       { label: 'Garantía Beretta',   cls: 'bg-gray-100 text-gray-700'      },
  puesta_marcha:          { label: 'Puesta en marcha',   cls: 'bg-purple-100 text-purple-800'  },
  cerrada:                { label: 'Cerrada',            cls: 'bg-gray-100 text-gray-500'      },
  cancelada:              { label: 'Cancelada',          cls: 'bg-gray-100 text-gray-400'      },
  rechazada:              { label: 'Rechazada',          cls: 'bg-gray-100 text-gray-400'      },
};

// Estados válidos para "En campo hoy" — todo excepto cerrada/cancelada
const ESTADOS_CAMPO_HOY = new Set([
  'en_ruta', 'garantia', 'instalacion',
  'cobrada', 'cobrada_sin_firma', 'ausente', 'pendiente',
  'pendiente_pago', 'pendiente_material', 'pendiente_presupuesto', 'pendiente_sustitucion',
  'garantia_ariston', 'garantia_nofer', 'garantia_svan', 'garantia_logman', 'garantia_beretta',
  'puesta_marcha',
]);

// Umbrales SLA (días) para mostrar badges de antigüedad en Por gestionar
const SLA_THRESHOLDS = {
  cobrada:              { warn: 1,  crit: 3  },
  cobrada_sin_firma:    { warn: 1,  crit: 2  },
  pendiente_pago:       { warn: 2,  crit: 5  },
  pendiente_material:   { warn: 5,  crit: 14 },
  pendiente_presupuesto:{ warn: 3,  crit: 7  },
  pendiente_sustitucion:{ warn: 3,  crit: 7  },
  ausente:              { warn: 3,  crit: 7  },
  pendiente:            { warn: 5,  crit: 14 },
  default:              { warn: 7,  crit: 14 },
};

function formatWaPhone(raw) {
  const digits = raw.replace(/\D/g, '');
  return digits.startsWith('34') ? digits : `34${digits}`;
}

function diasDesdeUpdate(orden) {
  // orden.updatedAt/createdAt son del esquema Firebase-nativo — las órdenes
  // raw recién sincronizadas de StelOrder no los tienen, solo
  // 'utc-last-modification-date'/'creation-date'. Sin este fallback,
  // cualquier orden con esquema raw daba antigüedad 0 siempre.
  const ref = orden.updatedAt || orden.createdAt || orden['utc-last-modification-date'] || orden['creation-date'];
  if (!ref) return 0;
  return Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
}

function SlaBadge({ orden }) {
  const dias = diasDesdeUpdate(orden);
  const thr  = SLA_THRESHOLDS[orden.estado] || SLA_THRESHOLDS.default;
  if (dias < thr.warn) return null;
  const isCrit = dias >= thr.crit;
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isCrit ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
      {dias}d
    </span>
  );
}

// Grupos de gestión: qué hace la oficina con cada tipo de resultado — se
// consultan desde la pestaña "Gestión" de cada orden (antes también
// alimentaban una bandeja agrupada "Por gestionar", retirada porque
// duplicaba lo que ya hace esta misma pestaña, orden a orden).
const GRUPOS_GESTION = [
  {
    key: 'verificar_cobro',
    label: 'Verificar cobro (sin firma)',
    desc: 'El técnico cobró pero el cliente no firmó. Confirmar con el cliente.',
    estados: new Set(['cobrada_sin_firma']),
    headerCls: 'border-yellow-200 bg-yellow-50',
    labelCls:  'text-yellow-800',
    dotCls:    'bg-yellow-500',
    btnCls:    'bg-yellow-600 hover:bg-yellow-700 text-white',
    btnLabel:  '✓ Cobro verificado',
    nextEstado: 'cobrada',
  },
  {
    key: 'facturar',
    label: 'Pendientes de factura',
    desc: 'El técnico cobró en visita. Genera la factura directamente en Firebase con numeración automática ZAP-F.',
    estados: new Set(['cobrada']),
    headerCls: 'border-emerald-200 bg-emerald-50',
    labelCls:  'text-emerald-800',
    dotCls:    'bg-emerald-500',
    btnCls:    'bg-emerald-600 hover:bg-emerald-700 text-white',
    btnLabel:  '📄 Generar factura',
    nextEstado: 'cerrada',
    nextField:  { facturada: true },
  },
  {
    key: 'pago',
    label: 'Reclamar cobro',
    desc: 'El cliente no pudo pagar durante la visita.',
    estados: new Set(['pendiente_pago']),
    headerCls: 'border-red-200 bg-red-50',
    labelCls:  'text-red-800',
    dotCls:    'bg-red-500',
    btnCls:    'bg-red-600 hover:bg-red-700 text-white',
    btnLabel:  '💳 Cobro recuperado',
    nextEstado: 'cobrada',
  },
  {
    key: 'material',
    label: 'Pedir material',
    desc: 'El técnico necesita piezas. Hacer pedido al proveedor.',
    estados: new Set(['pendiente_material']),
    headerCls: 'border-orange-200 bg-orange-50',
    labelCls:  'text-orange-800',
    dotCls:    'bg-orange-500',
    btnCls:    'bg-orange-600 hover:bg-orange-700 text-white',
    btnLabel:  '📦 Pedido realizado',
    nextEstado: 'pendiente',
  },
  {
    key: 'presupuesto',
    label: 'Enviar presupuesto',
    desc: 'Elaborar y enviar presupuesto al cliente.',
    estados: new Set(['pendiente_presupuesto']),
    headerCls: 'border-purple-200 bg-purple-50',
    labelCls:  'text-purple-800',
    dotCls:    'bg-purple-500',
    btnCls:    'bg-purple-600 hover:bg-purple-700 text-white',
    btnLabel:  '📄 Presupuesto enviado',
    nextEstado: 'cerrada',
  },
  {
    key: 'garantia',
    label: 'Gestionar con fabricante',
    desc: 'Subir parte a plataforma del fabricante y generar albarán.',
    estados: new Set(['garantia_ariston', 'garantia_nofer', 'garantia_svan', 'garantia_logman', 'garantia_beretta']),
    headerCls: 'border-gray-200 bg-gray-50',
    labelCls:  'text-gray-800',
    dotCls:    'bg-gray-500',
    btnCls:    'bg-gray-700 hover:bg-gray-800 text-white',
    btnLabel:  '✓ Gestionado con fabricante',
    nextEstado: 'cerrada',
    nextField:  { gestionadaFabricante: true },
  },
  {
    key: 'sustitucion',
    label: 'Sustitución en garantía',
    desc: 'Equipo a sustituir bajo garantía. Gestionar sustitución con el fabricante.',
    estados: new Set(['pendiente_sustitucion']),
    headerCls: 'border-cyan-200 bg-cyan-50',
    labelCls:  'text-cyan-800',
    dotCls:    'bg-cyan-500',
    btnCls:    'bg-cyan-700 hover:bg-cyan-800 text-white',
    btnLabel:  '✓ Sustitución gestionada con fabricante',
    nextEstado: 'cerrada',
    nextField:  { gestionadaFabricante: true },
  },
  {
    key: 'ausente',
    label: 'Ausente — Contactar cliente',
    desc: 'El técnico fue pero el cliente no estaba. Contactar antes de reprogramar.',
    estados: new Set(['ausente']),
    headerCls: 'border-yellow-200 bg-yellow-50',
    labelCls:  'text-yellow-800',
    dotCls:    'bg-yellow-500',
    btnCls:    'bg-yellow-600 hover:bg-yellow-700 text-white',
    btnLabel:  '📅 Contactado — Reprogramar',
    isReprogramar: true,
  },
  {
    key: 'pendiente',
    label: 'Sin realizar — Reprogramar',
    desc: 'La visita estaba en ruta pero no llegó a realizarse ese día.',
    estados: new Set(['pendiente']),
    headerCls: 'border-gray-200 bg-gray-50',
    labelCls:  'text-gray-700',
    dotCls:    'bg-gray-400',
    btnCls:    'bg-gray-600 hover:bg-gray-700 text-white',
    btnLabel:  '📅 Reprogramar',
    isReprogramar: true,
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function Spinner({ sm }) {
  return (
    <div className={`${sm ? 'w-4 h-4 border-2' : 'w-8 h-8 border-2'} border-gray-200 border-t-red-500 rounded-full animate-spin`} />
  );
}

function EstadoBadge({ estado }) {
  const c = ESTADO_CFG[estado] || { label: estado || '—', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>;
}

function TipoBadge({ tipo }) {
  const c = TIPO_CFG[tipo] || { label: tipo || '—', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>;
}

function TecnicoBadge({ nombre }) {
  const cls = TECNICO_BADGE[nombre] || 'bg-gray-100 text-gray-600';
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{nombre}</span>;
}

// Caja de campo estilo StelOrder — usada en la cabecera del detalle de orden
// (grid de 2 columnas, campos siempre visibles como formulario, editables o
// no según la caja). `children` sustituye a `value` cuando el contenido no
// es texto plano (p.ej. un badge de estado).
function CampoBox({ label, value, children, mono }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2">
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-1">{label}</p>
      {children || <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>}
    </div>
  );
}

// Pestaña "Firma" — estilo StelOrder (Nombre/NIF + imagen de firma +
// información). orden.firmaUrl lo escribe finalizarVisita.js desde hace
// pocos días (sube la firma a Storage) — antes de esta pestaña, nunca se
// mostraba en el panel de oficina. Órdenes anteriores a ese cambio no
// tienen el campo: degrada con un aviso, no rompe con una imagen inexistente.
function FirmaTab({ orden, n }) {
  return (
    <div className="max-w-sm space-y-3">
      <CampoBox label="Nombre" value={n.clienteNombre} />
      <CampoBox label="NIF" value={orden.dniCliente} mono />
      <div>
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Firma</p>
        {orden.firmaUrl ? (
          <a href={orden.firmaUrl} target="_blank" rel="noreferrer"
            className="block w-full h-40 border border-gray-200 rounded-xl overflow-hidden bg-white hover:opacity-90 transition-opacity">
            <img src={orden.firmaUrl} alt="Firma del cliente" className="w-full h-full object-contain" />
          </a>
        ) : (
          <div className="w-full h-40 border border-dashed border-gray-200 rounded-xl flex items-center justify-center text-xs text-gray-400 bg-gray-50">
            Sin firma registrada
          </div>
        )}
      </div>
      {orden.motivoSinFirma && (
        <div>
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Información de firma</p>
          <p className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">{orden.motivoSinFirma}</p>
        </div>
      )}
    </div>
  );
}

// Pestaña "Relacionados" — estilo StelOrder. Reúne dos cosas que antes
// estaban dispersas: el vínculo con la incidencia de origen
// (orden.parentIncidentId, ya sincronizado desde hace tiempo pero solo
// visible en KanbanComercialView) y el historial de cambios de estado,
// que antes vivía dentro de Gestión — es solo un traslado de sitio, el
// refresco sigue disparándose desde handleAccion igual que siempre.
function RelacionadosTab({ orden, historial }) {
  return (
    <div className="space-y-4">
      {orden.parentIncidentId && (
        <div className="border border-blue-100 bg-blue-50 rounded-xl px-4 py-3">
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-0.5">Incidencia de origen</p>
          <p className="text-sm text-blue-800 font-semibold">#{orden.parentIncidentId}</p>
        </div>
      )}

      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Historial</p>
        </div>
        {historial.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {historial.map((h, i) => {
              const cfg = ESTADO_CFG[h.estadoNuevo] || { label: h.estadoNuevo || '—', cls: 'bg-gray-100 text-gray-600' };
              const fuente = h.fuente || 'sistema';
              const fuenteIcon = fuente === 'finalizarVisita' || fuente === 'cierre_ruta' || fuente === 'tecnico'
                ? '🔧'
                : fuente === 'oficina'
                ? '🏢'
                : '⚙️';
              return (
                <div key={i} className="px-4 py-2.5 flex items-center gap-2.5">
                  <span title={fuente} className="text-sm leading-none flex-shrink-0">{fuenteIcon}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                  <span className="text-xs text-gray-400 flex-1 truncate">{h.usuario || 'Sistema'}</span>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{h.fecha || ''}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 px-4 py-3">Sin movimientos registrados</p>
        )}
      </div>
    </div>
  );
}

const ADJUNTO_TIPOS_PERMITIDOS = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
  'application/pdf': 'pdf', 'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};
const ADJUNTO_MAX_BYTES = 8 * 1024 * 1024;

function adjuntoIcono(tipo) {
  if (tipo === 'application/pdf') return '📄';
  if (tipo?.startsWith('image/')) return '🖼️';
  if (tipo?.includes('word')) return '📝';
  if (tipo?.includes('excel') || tipo?.includes('sheet')) return '📊';
  return '📎';
}
function adjuntoTamano(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Pestaña "Adjuntos" — única de las nuevas pestañas con funcionalidad real
// detrás (las demás solo muestran datos que ya existían). Sube a
// adjuntos_orden/{ordenId}/... vía subirAdjuntoOrden.js y guarda la
// metadata en orden.adjuntos[] con patchOrden (saveOrden.js no tiene
// allowlist de campos, así que este array nuevo no requirió tocarlo).
// Eliminar un adjunto solo lo quita del array — NO borra el objeto en
// Storage. Es deuda técnica intencional de esta v1: el archivo huérfano no
// cuesta nada relevante y borrar de Storage habría exigido otro endpoint
// con su propia autenticación.
function AdjuntosTab({ orden, onOrdenChange }) {
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [arrastrando, setArrastrando] = useState(false);
  const adjuntos = Array.isArray(orden.adjuntos) ? orden.adjuntos : [];

  const subirArchivos = async (files) => {
    setError('');
    for (const file of Array.from(files || [])) {
      const contentType = file.type;
      if (!ADJUNTO_TIPOS_PERMITIDOS[contentType]) {
        setError(`"${file.name}": formato no admitido (JPG, PNG, WEBP, PDF, DOC, DOCX, XLS, XLSX).`);
        continue;
      }
      if (file.size > ADJUNTO_MAX_BYTES) {
        setError(`"${file.name}" pesa ${(file.size / 1024 / 1024).toFixed(1)}MB — el máximo es 8MB.`);
        continue;
      }
      setSubiendo(true);
      try {
        const fileBase64 = await fileToBase64(file);
        const resp = await fetch('/.netlify/functions/subirAdjuntoOrden', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ fileBase64, contentType, ordenId: orden.id, nombre: file.name }),
        });
        const data = await resp.json();
        if (!data.success) { setError(data.error || `No se pudo subir "${file.name}"`); continue; }
        const nuevos = [...adjuntos, data.adjunto];
        await patchOrden(orden, { adjuntos: nuevos });
        onOrdenChange({ ...orden, adjuntos: nuevos });
      } catch (err) {
        setError(err.message || `No se pudo subir "${file.name}"`);
      } finally {
        setSubiendo(false);
      }
    }
  };

  const handleEliminarAdjunto = async (idx) => {
    if (!window.confirm('¿Eliminar este adjunto? Solo se quita del listado, el archivo no se borra de Storage.')) return;
    const nuevos = adjuntos.filter((_, i) => i !== idx);
    try {
      await patchOrden(orden, { adjuntos: nuevos });
      onOrdenChange({ ...orden, adjuntos: nuevos });
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <label
        onDragOver={e => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={e => { e.preventDefault(); setArrastrando(false); subirArchivos(e.dataTransfer.files); }}
        className={`block border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-colors ${
          arrastrando ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
        }`}
      >
        <input
          type="file" multiple className="hidden"
          accept={Object.keys(ADJUNTO_TIPOS_PERMITIDOS).join(',')}
          onChange={e => { subirArchivos(e.target.files); e.target.value = ''; }}
        />
        {subiendo ? (
          <p className="text-sm text-gray-500 font-semibold">Subiendo…</p>
        ) : (
          <>
            <p className="text-sm text-gray-500 font-semibold">Arrastra archivos aquí o haz clic para elegirlos</p>
            <p className="text-[11px] text-gray-400 mt-0.5">JPG, PNG, WEBP, PDF, DOC, DOCX, XLS, XLSX — máx. 8MB</p>
          </>
        )}
      </label>

      {error && <p className="text-xs text-red-600 font-semibold">{error}</p>}

      {adjuntos.length > 0 ? (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <div className="divide-y divide-gray-50">
            {adjuntos.map((a, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center gap-2.5">
                <span className="text-sm leading-none flex-shrink-0">{adjuntoIcono(a.tipo)}</span>
                <a href={a.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate">{a.nombre || 'Archivo'}</a>
                <span className="text-[11px] text-gray-400 flex-shrink-0">{adjuntoTamano(a.tamano)}</span>
                <span className="text-[11px] text-gray-400 flex-shrink-0 hidden sm:inline">{a.fecha ? a.fecha.slice(0, 10) : ''}</span>
                <button onClick={() => handleEliminarAdjunto(i)} title="Eliminar" className="text-gray-300 hover:text-red-500 flex-shrink-0 px-1">✕</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400 text-center py-2">Sin adjuntos</p>
      )}
    </div>
  );
}

// Normaliza campos que pueden venir con nombres StelOrder o Firebase
function norm(o) {
  return {
    referencia:    o.referencia      || o['full-reference']      || '',
    clienteNombre: o.clienteNombre   || o.clientName             || '',
    clientePhone:  o.clienteTelefono || o.clientPhone || o.clientePhone || '',
    direccion:     o.direccionTexto  || o.addressText  || o.direccion   || '',
    firmado:       o.firmado         || o.signed                 || o.firmada || false,
    total:         o['total-amount'] ?? o.totalAmount            ?? null,
    lines:         o.lines           || o.lineas                 || [],
    observaciones: o.observaciones   || o.comments               || o['private-comments'] || '',
  };
}

async function patchOrden(orden, patch) {
  const resp = await fetch('/.netlify/functions/saveOrden', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
    body: JSON.stringify({
      id:             orden.id,
      clienteId:      orden.clienteId     || '',
      clienteNombre:  orden.clienteNombre || orden.clientName || '',
      estadoAnterior: orden.estado        || null,
      stelorderId:    orden.stelorderId   || null,
      ...patch,
    }),
  });
  const data = await resp.json();
  if (!data.success) throw new Error(data.error || 'Error al guardar');
  return data;
}

function formaPagoLabel(o) {
  const payId = o['payment-option-id'];
  if (payId === 53268 || o.formaPago === 'efectivo') return 'Efectivo';
  if (payId === 156786 || o.formaPago === 'tarjeta')  return 'Tarjeta';
  if (payId === 92449)                                return 'Garantía';
  return '';
}

// ── ReprogramarModal ───────────────────────────────────────────────────────────

function ReprogramarModal({ orden, onClose, onDone, tecnicos }) {
  const [fecha,   setFecha]   = useState(orden.fechaProgramada || new Date().toISOString().split('T')[0]);
  const [tecnico, setTecnico] = useState(orden.tecnicoNombre || '');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [overlap, setOverlap] = useState(null);

  const tecnicoEfectivo = tecnico || orden.tecnicoNombre;

  // Detectar solapamientos al cambiar fecha o técnico
  useEffect(() => {
    if (!tecnicoEfectivo || !fecha) { setOverlap(null); return; }
    let cancelled = false;
    getDocs(query(
      collection(db, 'ordenes'),
      where('fechaProgramada', '==', fecha),
      where('tecnicoNombre', '==', tecnicoEfectivo)
    )).then(snap => {
      if (cancelled) return;
      const count = snap.docs.filter(d => d.id !== orden.id).length;
      setOverlap(count);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [fecha, tecnicoEfectivo, orden.id]);

  const handleGuardar = async () => {
    if (!fecha) return setError('Selecciona una fecha');
    setSaving(true); setError('');
    try {
      const tecnicoFinal = tecnico || orden.tecnicoNombre || '';
      const patch = {
        estado:          'en_ruta',
        fechaProgramada: fecha,
        notasHistorial:  `Reprogramado a ${fecha}${tecnicoFinal ? ` — ${tecnicoFinal}` : ''}`,
      };
      if (tecnico) patch.tecnicoNombre = tecnico;
      await patchOrden(orden, patch);
      onDone({ ...orden, ...patch });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-5 py-4 border-b flex items-center gap-3">
          <h3 className="font-bold text-gray-900">Reprogramar visita</h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          {overlap > 2 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2 text-xs font-semibold">
              ⚠️ {tecnicoEfectivo} ya tiene {overlap} orden{overlap !== 1 ? 'es' : ''} ese día. Revisa la carga.
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Nueva fecha *</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              Técnico <span className="text-gray-400 normal-case font-normal">(actual: {orden.tecnicoNombre || 'sin asignar'})</span>
            </label>
            <select value={tecnico} onChange={e => setTecnico(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400">
              <option value="">Mantener mismo técnico</option>
              {(tecnicos || TECNICOS_FALLBACK).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end border-t pt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleGuardar} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
            {saving && <Spinner sm />}Reprogramar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LineasEditorPanel ──────────────────────────────────────────────────────────

const LINE_ICONS = { desplazamiento: '🚗', mano_obra: '🔨', material: '📦' };

// Cabecera de columnas de la tabla de líneas — estilo StelOrder
// (Referencia/Nombre/Precio base/Uds./Subtotal). Solo se pinta con líneas
// presentes; en pantallas estrechas se oculta y cada fila sigue siendo
// legible por sus propios labels (ver LineItemRow).
function LineasTableHead() {
  return (
    <div className="hidden sm:flex items-center gap-2 px-2.5 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
      <span className="w-5 flex-shrink-0" />
      <span className="w-20 flex-shrink-0">Referencia</span>
      <span className="flex-1 min-w-0">Descripción</span>
      <span className="w-20 flex-shrink-0 text-right">Precio base</span>
      <span className="w-14 flex-shrink-0 text-center">Uds.</span>
      <span className="w-16 flex-shrink-0 text-right">Subtotal</span>
      <span className="w-6 flex-shrink-0" />
    </div>
  );
}

function LineItemRow({ line, index, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-2.5">
      <span className="text-base flex-shrink-0 w-5 text-center">{LINE_ICONS[line.tipo] || '📦'}</span>
      <span className="w-20 flex-shrink-0 text-[10px] text-gray-400 font-mono truncate" title={line.referencia}>
        {line.referencia || '—'}
      </span>
      <input
        type="text"
        value={line.descripcion}
        onChange={e => onChange(index, 'descripcion', e.target.value)}
        placeholder="Descripción"
        className="flex-1 min-w-0 text-sm font-medium text-gray-800 bg-transparent border-none outline-none placeholder-gray-400"
      />
      <div className="relative w-20 flex-shrink-0">
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
        <input type="number" min="0" step="0.5" value={line.precioUnitario}
          onChange={e => onChange(index, 'precioUnitario', e.target.value)}
          className="w-full pl-5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-1 outline-none" />
      </div>
      <input type="number" min="0.5" step="0.5" value={line.cantidad}
        onChange={e => onChange(index, 'cantidad', e.target.value)}
        className="w-14 flex-shrink-0 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-1 text-center outline-none" />
      <span className="w-16 flex-shrink-0 text-xs font-bold text-gray-800 text-right">
        €{(Number(line.cantidad) * Number(line.precioUnitario)).toFixed(2)}
      </span>
      <button onClick={() => onRemove(index)} className="w-6 flex-shrink-0 p-1 text-gray-300 hover:text-red-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function normServicio(s) {
  return {
    _type:      'servicio',
    id:         s.id,
    nombre:     s.name || '',
    referencia: s.reference || s['full-reference'] || '',
    precio:     Number(s['sales-price'] ?? 0),
  };
}

// Traduce el resultado de buscarProductosPorTexto (esquema StelOrder:
// name/reference/sales-price/real-stock...) al vocabulario en español que
// usa este panel internamente — ver AlmacenView.js.
const mapProductoStel = (p) => ({
  ...p,
  nombre: p.name || '', referencia: p.reference || p['full-reference'] || '',
  marca: p.marca || '', ean: p.barcode || '',
  precioVenta: p['sales-price'] ?? 0, stock: p['real-stock'] ?? 0,
});

function LineasEditorPanel({ orden, onDone, onCancel }) {
  const [lines, setLines]           = useState([]);
  const [productos, setProductos]   = useState([]);
  const [servicios, setServicios]   = useState([]);
  const [busqMat, setBusqMat]       = useState('');
  const [showMatDrop, setShowMatDrop] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');
  const [tarifas, setTarifas]       = useState({ manoObra: 45, desplazamiento: 20 });

  // Inicializar con líneas existentes
  useEffect(() => {
    const existing = (orden.lineas || orden.lines || []).map(l => ({
      tipo:           l.tipo          || 'material',
      descripcion:    l.descripcion   || l.nombre || l['item-name'] || l.description || l.name || '',
      referencia:     l.referencia    || '',
      cantidad:       Number(l.cantidad || l.quantity || l.units || 1),
      precioUnitario: Number(l.precioUnitario || l.unitPrice || l['item-base-price'] || l.price || 0),
      productoId:     l.productoId    || null,
      _isNew:         false,
    }));
    setLines(existing);
  }, []); // only on mount — intentional

  // Búsqueda bajo demanda contra Firestore para productos — nada de
  // precargar el catálogo completo (~22.700 productos reales de StelOrder),
  // ver buscarProductos.js. Servicios sí se cargan enteros: son solo ~44.
  useEffect(() => {
    if (busqMat.trim().length < 2) { setProductos([]); return; }
    let vivo = true;
    const t = setTimeout(() => {
      buscarProductosPorTexto(busqMat, { max: 4 }).then(rows => {
        if (vivo) setProductos(rows.map(mapProductoStel));
      }).catch(() => { if (vivo) setProductos([]); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busqMat]);

  useEffect(() => {
    getDocs(collection(db, 'servicios'))
      .then(snap => {
        const items = snap.docs.map(d => normServicio({ id: d.id, ...d.data() }));
        setServicios(items.filter(s => s.nombre).sort((a, b) => a.nombre.localeCompare(b.nombre)));
      })
      .catch(() => {});
    getDoc(doc(db, 'config', 'tarifas'))
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setTarifas(prev => ({
            manoObra:       d.manoObra       ?? prev.manoObra,
            desplazamiento: d.desplazamiento ?? prev.desplazamiento,
          }));
        }
      })
      .catch(() => {});
  }, []);

  const busqLower = busqMat.toLowerCase();
  const filteredProds = productos; // ya viene filtrado por buscarProductosPorTexto
  const filteredSvcs = busqMat.length > 1
    ? servicios.filter(s =>
        [s.nombre, s.referencia].some(v => v?.toLowerCase().includes(busqLower))
      ).slice(0, 3)
    : [];

  const handleBarcodeScan = async code => {
    setShowScanner(false);
    const rows = await buscarProductosPorTexto(code, { max: 4 }).catch(() => []);
    const found = rows.map(mapProductoStel).find(p => p.ean === code || p.referencia === code);
    if (found) addFromCatalog(found);
    else { setBusqMat(code); setShowMatDrop(true); }
  };

  const addFromCatalog = prod => {
    setLines(p => [...p, {
      tipo: 'material', descripcion: prod.nombre, referencia: prod.referencia || '',
      cantidad: 1, precioUnitario: prod.precioVenta || 0,
      productoId: prod.id, stockActual: prod.stock, _isNew: true,
    }]);
    setBusqMat(''); setShowMatDrop(false);
  };

  const addFromServicio = svc => {
    const tipo = /mano.*(obra)|mano obra/i.test(svc.nombre)
      ? 'mano_obra'
      : /desplaz/i.test(svc.nombre)
      ? 'desplazamiento'
      : 'material';
    setLines(p => [...p, {
      tipo, descripcion: svc.nombre, referencia: svc.referencia || '',
      cantidad: 1, precioUnitario: svc.precio,
      productoId: null, _isNew: true,
    }]);
    setBusqMat(''); setShowMatDrop(false);
  };

  const addManual = () =>
    setLines(p => [...p, { tipo: 'material', descripcion: '', referencia: '', cantidad: 1, precioUnitario: 0, productoId: null, _isNew: true }]);

  const addManoObra = () => {
    const svc = servicios.find(s => /mano.*(obra)|mano obra/i.test(s.nombre));
    setLines(p => [...p, {
      tipo: 'mano_obra',
      descripcion:    svc?.nombre || 'Mano de obra',
      referencia:     svc?.referencia || '',
      cantidad:       1,
      precioUnitario: svc ? svc.precio : tarifas.manoObra,
      productoId: null, _isNew: true,
    }]);
  };

  const addDesplazamiento = () => {
    const svc = servicios.find(s => /desplaz/i.test(s.nombre));
    setLines(p => [{
      tipo: 'desplazamiento',
      descripcion:    svc?.nombre || 'Desplazamiento',
      referencia:     svc?.referencia || '',
      cantidad:       1,
      precioUnitario: svc ? svc.precio : tarifas.desplazamiento,
      productoId: null, _isNew: true,
    }, ...p]);
  };

  const onChange = (i, field, value) =>
    setLines(p => p.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  const onRemove = i =>
    setLines(p => p.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + Number(l.cantidad) * Number(l.precioUnitario), 0);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const lineas = lines
        .filter(l => l.descripcion.trim())
        .map(({ _isNew, stockActual, ...l }) => ({
          ...l,
          cantidad:       Number(l.cantidad)       || 1,
          precioUnitario: Number(l.precioUnitario) || 0,
        }));

      await patchOrden(orden, { lineas });

      // Descontar stock para líneas nuevas del catálogo
      const newCatalog = lines.filter(l => l._isNew && l.productoId && l.descripcion.trim());
      const stockWarnings = [];
      for (const l of newCatalog) {
        const cant = Number(l.cantidad) || 1;
        try {
          // Comprobar stock actual antes de descontar
          const prodSnap = await getDoc(doc(db, 'productos', l.productoId));
          const stockActualizado = prodSnap.exists() ? (prodSnap.data()['real-stock'] ?? 0) : null;
          if (stockActualizado !== null && stockActualizado < cant) {
            stockWarnings.push(`${l.descripcion}: stock insuficiente (${stockActualizado} ud. disponibles, se usan ${cant})`);
          }
          await updateDoc(doc(db, 'productos', l.productoId), { 'real-stock': increment(-cant) });
          await addDoc(collection(db, 'movimientos_stock'), {
            productoId:     l.productoId,
            productoNombre: l.descripcion,
            tipo:           'uso_orden',
            cantidad:       cant,
            notas:          `Orden ${orden.referencia || orden.id} (editado desde oficina)`,
            fecha:          serverTimestamp(),
          });
        } catch (stockErr) {
          stockWarnings.push(`${l.descripcion}: error al descontar stock — ${stockErr.message}`);
        }
      }

      onDone({ ...orden, lineas, lines: lineas });
      if (stockWarnings.length > 0) {
        // Aviso no bloqueante: la orden ya fue guardada
        setMsg('Guardado. Avisos de stock: ' + stockWarnings.join(' | '));
        setSaving(false);
        return;
      }
    } catch (e) {
      setMsg('Error: ' + e.message);
      setSaving(false);
    }
  };

  return (
    <div className="border border-blue-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-100 flex items-center justify-between">
        <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Líneas</p>
        <button onClick={onCancel} className="text-xs text-gray-400 hover:text-gray-600 font-semibold">✕ Cancelar</button>
      </div>

      <div className="p-3 space-y-3 bg-gray-50">
        {/* Lista de líneas */}
        <div className="space-y-2">
          {lines.length > 0 && <LineasTableHead />}
          {lines.map((l, i) => (
            <LineItemRow key={i} line={l} index={i} onChange={onChange} onRemove={onRemove} />
          ))}
          {lines.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">Sin líneas — añade materiales o servicios</p>
          )}
        </div>

        {/* Botones rápidos */}
        <div className="flex flex-wrap gap-2">
          <button onClick={addManoObra}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-gray-300">
            🔨 Mano de obra
          </button>
          {!lines.some(l => l.tipo === 'desplazamiento') && (
            <button onClick={addDesplazamiento}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-gray-300">
              🚗 Desplazamiento
            </button>
          )}
        </div>

        {/* Escáner */}
        {showScanner && (
          <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} />
        )}

        {/* Buscador catálogo */}
        <div className="relative">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
            <button onClick={() => setShowScanner(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-700" title="Escanear EAN">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </button>
            <input
              type="text"
              value={busqMat}
              onChange={e => { setBusqMat(e.target.value); setShowMatDrop(true); }}
              onFocus={() => setShowMatDrop(true)}
              placeholder="Buscar material o escanear EAN…"
              className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
            />
            <button onClick={addManual} className="text-xs text-gray-400 hover:text-gray-700 font-medium flex-shrink-0">+ manual</button>
          </div>

          {showMatDrop && busqMat.length > 1 && (
            <div className="absolute top-full left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden">
              {filteredProds.length === 0 && filteredSvcs.length === 0 ? (
                <p className="text-sm text-gray-400 px-4 py-3">Sin resultados — usa "+ manual"</p>
              ) : (
                <>
                  {filteredProds.map(p => (
                    <button key={`prod-${p.id}`} onClick={() => addFromCatalog(p)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left border-b border-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{p.nombre}</p>
                        <p className="text-xs text-gray-400">{[p.referencia, p.marca].filter(Boolean).join(' · ')} · €{p.precioVenta || 0}</p>
                      </div>
                      <span className={`text-xs font-semibold flex-shrink-0 ${(p.stock ?? 0) <= (p.stockMinimo || 0) ? 'text-amber-600' : 'text-green-700'}`}>
                        {p.stock ?? '—'} ud.
                      </span>
                    </button>
                  ))}
                  {filteredSvcs.map(s => (
                    <button key={`svc-${s.id}`} onClick={() => addFromServicio(s)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 text-left border-b border-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{s.nombre}</p>
                        <p className="text-xs text-gray-400">{s.referencia || '—'} · €{s.precio}</p>
                      </div>
                      <span className="text-xs font-semibold text-purple-600 flex-shrink-0">Servicio</span>
                    </button>
                  ))}
                </>
              )}
              <button onClick={() => setShowMatDrop(false)} className="w-full text-xs text-gray-400 py-2 hover:bg-gray-50">Cerrar</button>
            </div>
          )}
        </div>

        {/* Total */}
        {lines.length > 0 && (
          <div className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-gray-100">
            <span className="text-sm font-semibold text-gray-600">Total</span>
            <span className="text-base font-bold text-gray-900">€{total.toFixed(2)}</span>
          </div>
        )}

        {msg && <p className="text-xs font-semibold text-red-600">{msg}</p>}

        <button onClick={handleSave} disabled={saving}
          className="w-full py-2.5 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
          {saving ? <><Spinner sm /> Guardando…</> : 'Guardar líneas'}
        </button>
      </div>
    </div>
  );
}

// ── useVisitas ─────────────────────────────────────────────────────────────────

function useVisitas(ordenId) {
  const [visitas, setVisitas] = useState([]);
  useEffect(() => {
    if (!ordenId) return;
    getDocs(query(
      collection(db, 'ordenes', ordenId, 'visitas'),
      orderBy('fecha', 'asc')
    ))
      .then(snap => setVisitas(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [ordenId]);
  return [visitas, setVisitas];
}

// ── NuevaVisitaForm ────────────────────────────────────────────────────────────

function NuevaVisitaForm({ orden, tecnicos, onDone, onCancel }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    fecha:              today,
    tecnicoNombre:      orden.tecnicoNombre || '',
    horaLlegada:        '',
    horaFin:            '',
    descripcionTrabajo: '',
    estado:             orden.estado || 'pendiente',
    formaPago:          '',
    importe:            '',
  });
  const [lineas,  setLineas]  = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const f    = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const iCls = 'w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-red-400';
  const lCls = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1';

  const addLinea    = () => setLineas(p => [...p, { descripcion: '', cantidad: 1, precioUnitario: 0 }]);
  const removeLinea = i  => setLineas(p => p.filter((_, idx) => idx !== i));
  const updateLinea = (i, k, v) => setLineas(p => p.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const totalLineas = lineas.reduce((s, l) => s + Number(l.cantidad) * Number(l.precioUnitario), 0);

  const handleSave = async () => {
    if (!form.descripcionTrabajo.trim() && lineas.filter(l => l.descripcion.trim()).length === 0)
      return setError('Describe el trabajo realizado o añade material');
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/saveVisita', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          ordenId:        orden.id,
          estadoAnterior: orden.estado,
          ...form,
          importe: form.importe ? Number(form.importe) : totalLineas > 0 ? totalLineas : null,
          lineas:  lineas.filter(l => l.descripcion.trim()).map(l => ({
            tipo:           'material',
            descripcion:    l.descripcion,
            cantidad:       Number(l.cantidad)       || 1,
            precioUnitario: Number(l.precioUnitario) || 0,
          })),
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error al guardar');
      onDone({ ...form, id: data.visitaId, lineas: lineas.filter(l => l.descripcion.trim()) });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-green-200 rounded-xl overflow-hidden">
      <div className="bg-green-50 px-4 py-2.5 border-b border-green-100 flex items-center justify-between">
        <p className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Registrar seguimiento / nueva visita</p>
        <button onClick={onCancel} className="text-xs text-gray-400 hover:text-gray-600 font-semibold">✕</button>
      </div>
      <div className="p-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs font-semibold">{error}</div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lCls}>Fecha *</label>
            <input type="date" value={form.fecha} onChange={f('fecha')} className={iCls} />
          </div>
          <div>
            <label className={lCls}>Técnico</label>
            <select value={form.tecnicoNombre} onChange={f('tecnicoNombre')} className={iCls}>
              <option value="">Sin asignar</option>
              {(tecnicos || TECNICOS_FALLBACK).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={lCls}>Hora llegada</label>
            <input type="time" value={form.horaLlegada} onChange={f('horaLlegada')} className={iCls} />
          </div>
          <div>
            <label className={lCls}>Hora fin</label>
            <input type="time" value={form.horaFin} onChange={f('horaFin')} className={iCls} />
          </div>
        </div>

        <div>
          <label className={lCls}>Trabajo realizado *</label>
          <textarea rows={3} value={form.descripcionTrabajo} onChange={f('descripcionTrabajo')}
            className={`${iCls} resize-none`}
            placeholder="Describe qué se hizo en esta visita…" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lCls}>Estado resultante</label>
            <select value={form.estado} onChange={f('estado')} className={iCls}>
              {Object.entries(ESTADO_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lCls}>Forma de pago</label>
            <select value={form.formaPago} onChange={f('formaPago')} className={iCls}>
              <option value="">Sin cobro</option>
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
              <option value="bizum">Bizum</option>
            </select>
          </div>
          {form.formaPago && (
            <div className="col-span-2">
              <label className={lCls}>Importe cobrado (€)</label>
              <input type="number" min="0" step="0.01" value={form.importe} onChange={f('importe')}
                className={iCls} placeholder="0.00" />
            </div>
          )}
        </div>

        {/* Líneas de material */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={lCls}>Material utilizado</label>
            <button onClick={addLinea}
              className="text-xs text-blue-500 hover:text-blue-700 font-semibold px-2 py-0.5 rounded hover:bg-blue-50">
              + Añadir línea
            </button>
          </div>
          {lineas.length > 0 ? (
            <div className="space-y-1.5">
              {lineas.map((l, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <input type="text" value={l.descripcion}
                    onChange={e => updateLinea(i, 'descripcion', e.target.value)}
                    placeholder="Material / servicio"
                    className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-red-400" />
                  <input type="number" min="0.5" step="0.5" value={l.cantidad}
                    onChange={e => updateLinea(i, 'cantidad', e.target.value)}
                    className="w-12 text-xs bg-white border border-gray-200 rounded px-1.5 py-1 text-center focus:outline-none" />
                  <span className="text-xs text-gray-400">×</span>
                  <div className="relative w-20">
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                    <input type="number" min="0" step="0.5" value={l.precioUnitario}
                      onChange={e => updateLinea(i, 'precioUnitario', e.target.value)}
                      className="w-full pl-4 text-xs bg-white border border-gray-200 rounded py-1 focus:outline-none" />
                  </div>
                  <button onClick={() => removeLinea(i)}
                    className="text-gray-300 hover:text-red-500 p-0.5 flex-shrink-0 leading-none">✕</button>
                </div>
              ))}
              {totalLineas > 0 && (
                <div className="flex justify-between pt-1 px-1">
                  <span className="text-xs text-gray-400">Total material</span>
                  <span className="text-xs font-bold text-gray-800">€{totalLineas.toFixed(2)}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2 bg-gray-50 rounded-lg">
              Sin materiales — pulsa + Añadir línea
            </p>
          )}
        </div>

        <button onClick={handleSave} disabled={saving}
          className="w-full py-2.5 text-sm font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
          {saving ? <><Spinner sm /> Guardando…</> : '✓ Registrar visita'}
        </button>
      </div>
    </div>
  );
}

// ── VisitaCard ─────────────────────────────────────────────────────────────────

function VisitaCard({ visita }) {
  const [expanded, setExpanded] = useState(false);
  const estadoCfg = ESTADO_CFG[visita.estado] || { label: visita.estado || '—', cls: 'bg-gray-100 text-gray-600' };
  const lineas = visita.lineas || [];
  const totalLineas = lineas.reduce((s, l) => s + Number(l.cantidad || 1) * Number(l.precioUnitario || 0), 0);
  const importeDisplay = visita.importe != null ? Number(visita.importe) : totalLineas;

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center gap-2.5 px-3.5 py-3 hover:bg-gray-50 transition-colors text-left"
        onClick={() => setExpanded(p => !p)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-gray-900">{visita.fecha}</span>
            {visita.tecnicoNombre && <TecnicoBadge nombre={visita.tecnicoNombre} />}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${estadoCfg.cls}`}>{estadoCfg.label}</span>
            {visita.formaPago && (
              <span className="text-[10px] text-gray-500 capitalize">{visita.formaPago}</span>
            )}
            {importeDisplay > 0 && (
              <span className="text-xs font-bold text-emerald-700 ml-auto">{importeDisplay.toFixed(2)} €</span>
            )}
          </div>
          {!expanded && visita.descripcionTrabajo && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{visita.descripcionTrabajo}</p>
          )}
        </div>
        <svg
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-2 border-t border-gray-50">
          {(visita.horaLlegada || visita.horaFin || visita.horasTrabajadas != null) && (
            <p className="text-xs text-gray-500 pt-2 flex gap-3 flex-wrap">
              {visita.horaLlegada   && <span>Llegada: <strong>{visita.horaLlegada}</strong></span>}
              {visita.horaFin       && <span>Fin: <strong>{visita.horaFin}</strong></span>}
              {visita.horasTrabajadas != null && <span><strong>{visita.horasTrabajadas}h</strong> trabajadas</span>}
            </p>
          )}
          {visita.descripcionTrabajo && (
            <p className="text-xs text-gray-700 bg-gray-50 rounded-lg px-3 py-2 leading-relaxed">
              {visita.descripcionTrabajo}
            </p>
          )}
          {lineas.length > 0 && (
            <div className="space-y-1">
              {lineas.map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2.5 py-1.5">
                  <span className="flex-1 text-gray-700 truncate">{l.descripcion}</span>
                  <span className="text-gray-500 font-semibold flex-shrink-0">
                    {l.cantidad} × {Number(l.precioUnitario || 0).toFixed(2)} €
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── VisitasPanel ───────────────────────────────────────────────────────────────

function VisitasPanel({ orden, tecnicos, onVisitaAdded }) {
  const [visitas, setVisitas] = useVisitas(orden.id);
  const [showForm, setShowForm] = useState(false);

  const handleDone = (nueva) => {
    setVisitas(p => [...p, nueva]);
    setShowForm(false);
    onVisitaAdded?.(nueva);
  };

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
          Visitas de seguimiento{visitas.length > 0 ? ` (${visitas.length})` : ''}
        </p>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-xs font-bold text-green-600 hover:text-green-800 px-2 py-0.5 rounded hover:bg-green-50 transition-colors"
          >
            + Nueva visita
          </button>
        )}
      </div>
      <div className="p-3 space-y-2">
        {visitas.length === 0 && !showForm && (
          <p className="text-xs text-gray-400 text-center py-3">Sin visitas de seguimiento registradas</p>
        )}
        {visitas.map(v => <VisitaCard key={v.id} visita={v} />)}
        {showForm && (
          <NuevaVisitaForm
            orden={orden}
            tecnicos={tecnicos}
            onDone={handleDone}
            onCancel={() => setShowForm(false)}
          />
        )}
      </div>
    </div>
  );
}

// ── OrdenDetallePanel ──────────────────────────────────────────────────────────

function OrdenDetallePanel({ orden: inicial, onClose, onUpdate, onReprogramar, onCancelar, onFacturar }) {
  const [orden,     setOrden]     = useState(inicial);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState('');
  const [historial, setHistorial] = useState([]);
  const [garantiaForm, setGarantiaForm] = useState({
    numeroCasoFabricante: inicial.numeroCasoFabricante || '',
    estadoGarantia:       inicial.estadoGarantia       || '',
  });
  const [savingGarantia, setSavingGarantia] = useState(false);
  const [garantiaError,  setGarantiaError]  = useState('');
  const [tab,            setTab]            = useState('mas_info');

  // ── Edición inline ──────────────────────────────────────────────────
  const tecnicos = useTecnicos();
  const [editing,     setEditing]     = useState(false);
  const [editForm,    setEditForm]    = useState({});
  const [savingEdit,  setSavingEdit]  = useState(false);
  const [editMsg,     setEditMsg]     = useState('');
  const [masOpen,     setMasOpen]     = useState(false); // menú "Más" del header (Imprimir/Cancelar)

  // ── Edición de líneas ────────────────────────────────────────────────
  const [editingLines, setEditingLines] = useState(false);

  // Al cambiar de orden (clic en otra de la lista compacta de la
  // izquierda) hay que resincronizar TODO el estado local del panel, no
  // solo `tab`: `orden` viene de useState(inicial), que solo lee el prop
  // en el montaje inicial — al no desmontarse el panel entre una orden y
  // otra (mismo componente, misma posición), sin este efecto se seguía
  // mostrando la orden anterior, como si el clic no hiciera nada. También
  // se cierra cualquier edición a medias: dejarla abierta significaría
  // guardar sobre la orden nueva datos pensados para la anterior.
  useEffect(() => {
    setOrden(inicial);
    setTab('mas_info');
    setEditing(false);
    setEditingLines(false);
    setEditMsg('');
    setMsg('');
    setGarantiaForm({
      numeroCasoFabricante: inicial.numeroCasoFabricante || '',
      estadoGarantia:       inicial.estadoGarantia       || '',
    });
    setGarantiaError('');
    setMasOpen(false);
  }, [inicial.id]); // eslint-disable-line

  const startEditLines = () => { setEditing(false); setEditingLines(true); };
  const cancelEditLines = () => setEditingLines(false);

  const handleLinesDone = updated => {
    setOrden(updated);
    onUpdate(updated);
    setEditingLines(false);
    setTab('mas_info');
    setEditMsg('Líneas guardadas');
    setTimeout(() => setEditMsg(''), 3000);
  };

  const startEdit = () => {
    const n = norm(orden);
    setEditingLines(false);
    setEditForm({
      clienteNombre:       n.clienteNombre || '',
      clientePhone:        n.clientePhone  || '',
      direccion:           n.direccion     || '',
      tecnicoNombre:       orden.tecnicoNombre       || '',
      fechaProgramada:     orden.fechaProgramada      || '',
      prioridad:           orden.prioridad           || 'normal',
      descripcion:         orden.descripcion         || orden.comments || '',
      comentariosPrivados: orden.comentariosPrivados || '',
    });
    setEditing(true);
    setEditMsg('');
  };

  const cancelEdit = () => { setEditing(false); setEditMsg(''); };

  const handleSaveEdit = async () => {
    setSavingEdit(true); setEditMsg('');
    try {
      const patch = {
        clienteNombre:      editForm.clienteNombre      || null,
        clientePhone:       editForm.clientePhone       || null,
        direccion:          editForm.direccion          || null,
        tecnicoNombre:      editForm.tecnicoNombre      || null,
        fechaProgramada:    editForm.fechaProgramada    || null,
        prioridad:          editForm.prioridad          || 'normal',
        descripcion:        editForm.descripcion        || null,
        comentariosPrivados: editForm.comentariosPrivados || null,
      };
      await patchOrden(orden, patch);
      const updated = { ...orden, ...patch };
      setOrden(updated);
      onUpdate(updated);
      setEditing(false);
      setEditMsg('Guardado correctamente');
      setTimeout(() => setEditMsg(''), 3000);

      // Sync datos de cliente a StelOrder + Firebase clientes si cambiaron
      const stelClienteId = orden.stelorderClienteId || orden['account-id'];
      const nombreAntes   = orden.clienteNombre   || orden.clientName  || '';
      const direccAntes   = orden.direccionTexto  || orden.addressText || '';
      const clienteCambio = editForm.clienteNombre !== nombreAntes || editForm.direccion !== direccAntes;
      if (stelClienteId && /^\d+$/.test(String(stelClienteId)) && clienteCambio && editForm.clienteNombre?.trim()) {
        fetch('/.netlify/functions/updateClient', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({
            clientId:       String(stelClienteId),
            nombreCompleto: editForm.clienteNombre.trim(),
            direccion:      editForm.direccion?.trim() || null,
          }),
        }).catch(e => console.warn('[handleSaveEdit] updateClient failed:', e.message));
      }
    } catch (e) {
      setEditMsg('Error: ' + e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Load historial subcollection
  useEffect(() => {
    if (!orden.id) return;
    getDocs(query(
      collection(db, 'ordenes', String(orden.id), 'historial'),
      orderBy('fecha', 'desc'),
      limit(20)
    )).then(snap => {
      setHistorial(snap.docs.map(d => d.data()));
    }).catch(() => {});
  }, [orden.id]);

  const handleGuardarGarantia = async () => {
    setSavingGarantia(true); setGarantiaError('');
    try {
      await patchOrden(orden, garantiaForm);
      setOrden(p => ({ ...p, ...garantiaForm }));
      onUpdate({ ...orden, ...garantiaForm });
    } catch (e) {
      setGarantiaError('Error al guardar: ' + e.message);
    } finally { setSavingGarantia(false); }
  };

  const grupo = GRUPOS_GESTION.find(g => g.estados.has(orden.estado));
  const n     = norm(orden);
  const pago  = formaPagoLabel(orden);

  const handleAccion = async () => {
    if (grupo?.isReprogramar) { onReprogramar(orden); return; }
    // "facturar" no es un simple cambio de estado — genera una factura real
    // con numeración ZAP-F vía FacturarModal (saveFactura.js), igual que
    // hacía la bandeja "Por gestionar" antes de retirarse.
    if (grupo?.key === 'facturar') { onFacturar(orden); return; }
    if (!grupo) return;
    setSaving(true); setMsg('');
    try {
      const patch = { estado: grupo.nextEstado, ...(grupo.nextField || {}) };
      await patchOrden(orden, patch);
      const updated = { ...orden, ...patch };
      setOrden(updated);
      onUpdate(updated);
      setMsg('Guardado correctamente');
      setTimeout(() => setMsg(''), 3000);
      // Refresh historial so the new audit entry is visible immediately
      getDocs(query(
        collection(db, 'ordenes', String(orden.id), 'historial'),
        orderBy('fecha', 'desc'),
        limit(20)
      )).then(snap => setHistorial(snap.docs.map(d => d.data()))).catch(() => {});
      // Sync cierre a StelOrder (best-effort)
      if (grupo.nextEstado === 'cerrada' && orden.stelorderId && /^\d+$/.test(String(orden.stelorderId))) {
        fetch('/.netlify/functions/updateWorkorderState', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ workorderId: orden.stelorderId, newStateId: 651428 }),
        }).catch(() => {});
      }
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const ORDEN_TABS = [
    { id: 'mas_info', label: 'Más información' },
    { id: 'lineas',   label: 'Líneas' },
    { id: 'firma',    label: 'Firma' },
    { id: 'adjuntos', label: 'Adjuntos' },
    { id: 'relacionados', label: 'Relacionados' },
    { id: 'visitas',  label: 'Visitas' },
    { id: 'gestion',  label: 'Gestión', alert: !!grupo && !['cerrada', 'cancelada'].includes(orden.estado) },
  ];

  return (
    <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden max-h-[calc(100vh-160px)]">

        {/* Header — layout de 2 columnas al estilo StelOrder: los campos
            quedan siempre visibles como cajas (CampoBox), no solo un
            resumen de una línea. El Estado es de solo lectura aquí (un
            badge, no un desplegable) — cambiarlo de verdad sigue siendo
            cosa exclusiva de la pestaña Gestión, para no saltarse el motor
            de reglas GRUPOS_GESTION. */}
        <div className="px-5 py-4 border-b flex-shrink-0 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button onClick={onClose} className="flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 -ml-1 px-1.5 py-1 rounded-lg hover:bg-gray-100">
              ← Volver
            </button>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!editing && (
                <button
                  onClick={startEdit}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50"
                >✏️ Editar</button>
              )}
              <div className="relative">
                <button
                  onClick={() => setMasOpen(v => !v)}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50"
                >Más ▾</button>
                {masOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMasOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-20">
                      <button
                        onClick={() => { setMasOpen(false); imprimirOrden(orden); }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >🖨️ Imprimir</button>
                      {!['cerrada', 'cancelada'].includes(orden.estado) && (
                        <button
                          onClick={() => { setMasOpen(false); onCancelar?.(orden); }}
                          className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                        >✕ Cancelar orden</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-bold text-gray-900 font-mono text-base">{n.referencia || '—'}</p>
            {orden.prioridad === 'urgente' && (
              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">URGENTE</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <CampoBox label="Cliente" value={n.clienteNombre} />
              <CampoBox label="Teléfono">
                {n.clientePhone ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={`tel:${n.clientePhone}`} className="text-sm text-gray-800 font-mono hover:text-gray-900">{n.clientePhone}</a>
                    {/^[67]\d{8}$|^34[67]\d{8}$/.test(n.clientePhone.replace(/\D/g, '')) && (
                      <a href={`https://wa.me/${formatWaPhone(n.clientePhone)}`} target="_blank" rel="noreferrer"
                        className="text-[10px] text-green-600 hover:text-green-800 font-bold bg-green-50 px-1.5 py-0.5 rounded-full">
                        WhatsApp →
                      </a>
                    )}
                  </div>
                ) : <p className="text-sm text-gray-400">—</p>}
              </CampoBox>
              <CampoBox label="Dirección">
                {n.direccion ? (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(n.direccion)}`}
                    target="_blank" rel="noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-start gap-1">
                    📍 <span className="truncate">{n.direccion}</span>
                  </a>
                ) : <p className="text-sm text-gray-400">—</p>}
              </CampoBox>
            </div>
            <div className="space-y-2">
              <CampoBox label="Fecha">
                <p className="text-sm text-gray-800">{orden.fechaProgramada || '—'}</p>
                {orden.horaInicio && <p className="text-xs text-gray-500 mt-0.5">{orden.horaInicio}</p>}
              </CampoBox>
              <CampoBox label="Estado"><EstadoBadge estado={orden.estado} /></CampoBox>
              <CampoBox label="Técnico">
                {orden.tecnicoNombre ? <TecnicoBadge nombre={orden.tecnicoNombre} /> : <p className="text-sm text-gray-400">Sin asignar</p>}
              </CampoBox>
              <CampoBox label="Tipo"><TipoBadge tipo={orden.tipo} /></CampoBox>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0 overflow-x-auto">
          {ORDEN_TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap flex-shrink-0 border-b-2 transition-colors ${tab === t.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.alert && <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ──── TAB MÁS INFORMACIÓN ──── */}
          {tab === 'mas_info' && (<>

          {/* Mensaje de edición */}
          {editMsg && (
            <p className={`text-xs font-semibold px-3 py-2 rounded-xl ${editMsg.startsWith('Error') ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
              {editMsg}
            </p>
          )}

          {/* ── Formulario de edición inline ── */}
          {editing && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Editar datos de la orden</p>

              {/* Cliente */}
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Nombre cliente</label>
                <input value={editForm.clienteNombre}
                  onChange={e => setEditForm(p => ({ ...p, clienteNombre: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Teléfono</label>
                  <input value={editForm.clientePhone}
                    onChange={e => setEditForm(p => ({ ...p, clientePhone: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Prioridad</label>
                  <select value={editForm.prioridad}
                    onChange={e => setEditForm(p => ({ ...p, prioridad: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400">
                    <option value="normal">Normal</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Dirección</label>
                <input value={editForm.direccion}
                  onChange={e => setEditForm(p => ({ ...p, direccion: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Técnico</label>
                  <select value={editForm.tecnicoNombre}
                    onChange={e => setEditForm(p => ({ ...p, tecnicoNombre: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400">
                    <option value="">Sin asignar</option>
                    {tecnicos.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Fecha programada</label>
                  <input type="date" value={editForm.fechaProgramada}
                    onChange={e => setEditForm(p => ({ ...p, fechaProgramada: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Descripción</label>
                <textarea rows={2} value={editForm.descripcion}
                  onChange={e => setEditForm(p => ({ ...p, descripcion: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 resize-none" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Notas internas</label>
                <textarea rows={2} value={editForm.comentariosPrivados}
                  onChange={e => setEditForm(p => ({ ...p, comentariosPrivados: e.target.value }))}
                  placeholder="Solo visible en la oficina…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 resize-none" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={cancelEdit}
                  className="flex-1 py-2 text-sm font-bold border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 bg-white">
                  Cancelar
                </button>
                <button onClick={handleSaveEdit} disabled={savingEdit}
                  className="flex-1 py-2 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                  {savingEdit ? <><Spinner sm /> Guardando…</> : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* Cliente/Teléfono/Dirección/Técnico/Fecha ya se ven siempre en
              la cabecera (CampoBox) — quitado de aquí para no duplicar. */}

          {/* Equipo + fotos */}
          {(orden.equipmentInfo || orden.assetSerialNumber || orden.assetImageUrls?.length > 0) && (
            <div className="bg-gray-50 rounded-xl p-3.5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Equipo</p>
              {orden.equipmentInfo && (
                <p className="text-sm font-semibold text-gray-800">{orden.equipmentInfo}</p>
              )}
              {orden.assetSerialNumber && (
                <p className="text-xs text-gray-500 font-mono mt-0.5">Serie: {orden.assetSerialNumber}</p>
              )}
              {orden.assetImageUrls?.length > 0 && (
                <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                  {orden.assetImageUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer"
                      className="block aspect-square rounded-lg overflow-hidden bg-gray-200 hover:opacity-90 transition-opacity">
                      <img
                        src={url}
                        alt={`Foto ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Resultado de la visita ── */}
          {(n.firmado || n.observaciones || n.lines.length > 0 || n.total !== null || orden.dniCliente || orden.justificacion) && (
            <div className="border border-blue-100 rounded-xl overflow-hidden">
              <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-100">
                <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Resultado de la visita</p>
              </div>
              <div className="p-4 space-y-4">

                {/* Firma + DNI + Importe */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${n.firmado ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      {n.firmado ? '✓' : '✗'}
                    </div>
                    <span className="text-xs text-gray-600 font-semibold">Firma</span>
                  </div>
                  {orden.dniCliente && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold bg-emerald-100 text-emerald-700">✓</div>
                      <span className="text-xs font-mono font-bold text-gray-700">{orden.dniCliente}</span>
                    </div>
                  )}
                  {n.total !== null && n.total > 0 && (
                    <div className="ml-auto text-right">
                      <p className="text-base font-black text-gray-900">{n.total.toFixed(2)} €</p>
                      {pago && <p className="text-xs text-gray-500">{pago}</p>}
                    </div>
                  )}
                </div>

                {/* Observaciones del técnico */}
                {n.observaciones && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Observaciones del técnico</p>
                    <p className="text-sm text-gray-700 leading-relaxed bg-white border border-gray-100 rounded-xl px-3 py-2.5">{n.observaciones}</p>
                  </div>
                )}

                {/* Material utilizado */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        Material utilizado {n.lines.length > 0 ? `(${n.lines.length})` : ''}
                      </p>
                      <button onClick={() => setTab('lineas')}
                        className="text-[10px] font-bold text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50">
                        ✏️ Editar →
                      </button>
                    </div>
                    {n.lines.length > 0 ? (
                      <div className="space-y-1">
                        {n.lines.map((l, i) => {
                          const TIPO_ICONS = { desplazamiento: '🚗', mano_obra: '🔨', material: '📦' };
                          const nombre = l.descripcion || l.nombre || l['item-name'] || l.description || l.name || `Línea ${i + 1}`;
                          const cant   = Number(l.cantidad || l.quantity || l.units || 1);
                          const precio = Number(l.precioUnitario || l.unitPrice || l['item-base-price'] || l.price || 0);
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs bg-white border border-gray-100 rounded-lg px-2.5 py-1.5">
                              <span className="flex-shrink-0">{TIPO_ICONS[l.tipo] || '📦'}</span>
                              <span className="text-gray-700 flex-1 min-w-0 truncate">{nombre}</span>
                              {l.referencia && <span className="text-gray-400 font-mono flex-shrink-0">{l.referencia}</span>}
                              <span className="text-gray-500 font-semibold flex-shrink-0">{cant} × {precio.toFixed(2)} €</span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between pt-1 px-1">
                          <span className="text-xs text-gray-400">Total material</span>
                          <span className="text-sm font-bold text-gray-900">
                            €{n.lines.reduce((s, l) => s + (Number(l.cantidad || l.quantity || l.units || 1) * Number(l.precioUnitario || l.unitPrice || l['item-base-price'] || l.price || 0)), 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Sin líneas — ve a la pestaña Material para añadir</p>
                    )}
                  </div>

                {/* Material faltante */}
                {Array.isArray(orden.materialFaltante) && orden.materialFaltante.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-orange-500 uppercase tracking-wider mb-1.5">Material faltante</p>
                    <div className="space-y-1">
                      {orden.materialFaltante.map((m, i) => (
                        <div key={i} className="text-xs bg-orange-50 border border-orange-100 rounded-lg px-2.5 py-1.5 text-orange-800">
                          {typeof m === 'object' ? (m.nombre || m.name || JSON.stringify(m)) : m}
                          {typeof m === 'object' && m.referencia && (
                            <span className="ml-1.5 font-mono text-orange-600">{m.referencia}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nuevo activo (sustitución) */}
                {orden.nuevoActivo && typeof orden.nuevoActivo === 'object' && (
                  <div>
                    <p className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider mb-1.5">Equipo a sustituir — datos del nuevo</p>
                    <div className="bg-cyan-50 border border-cyan-100 rounded-xl px-3 py-2.5 space-y-1">
                      {orden.nuevoActivo.marca && (
                        <p className="text-xs text-gray-700"><span className="font-semibold">Marca:</span> {orden.nuevoActivo.marca}</p>
                      )}
                      {orden.nuevoActivo.modelo && (
                        <p className="text-xs text-gray-700"><span className="font-semibold">Modelo:</span> {orden.nuevoActivo.modelo}</p>
                      )}
                      {orden.nuevoActivo.numeroSerie && (
                        <p className="text-xs text-gray-700 font-mono"><span className="font-semibold not-italic">Serie:</span> {orden.nuevoActivo.numeroSerie}</p>
                      )}
                      {orden.nuevoActivo.tipoEquipo && (
                        <p className="text-xs text-gray-700"><span className="font-semibold">Tipo:</span> {orden.nuevoActivo.tipoEquipo}</p>
                      )}
                      {orden.nuevoActivo.notas && (
                        <p className="text-xs text-gray-600 italic mt-1">{orden.nuevoActivo.notas}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Justificación (ausente) */}
                {orden.justificacion && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Justificación</p>
                    <p className="text-sm text-gray-700 bg-white border border-gray-100 rounded-xl px-3 py-2.5">{orden.justificacion}</p>
                  </div>
                )}

                {/* Motivo sin firma */}
                {orden.motivoSinFirma && (
                  <div>
                    <p className="text-[10px] font-bold text-yellow-600 uppercase tracking-wider mb-1.5">Motivo sin firma</p>
                    <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2.5">{orden.motivoSinFirma}</p>
                  </div>
                )}

                {/* Tiempos de visita */}
                {(orden.horaLlegada || orden.horaFin) && (
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {orden.horaLlegada && <span>Llegada: <strong>{orden.horaLlegada}</strong></span>}
                    {orden.horaFin     && <span>Fin: <strong>{orden.horaFin}</strong></span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fotos de la visita — las sube el técnico en Finalizar visita
              (subirFotoVisita.js, a Firebase Storage); antes de esto no
              había ningún sitio del lado de oficina para verlas. */}
          {Array.isArray(orden.fotos) && orden.fotos.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Fotos de la visita ({orden.fotos.length})</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {orden.fotos.map((url, i) => (
                  <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                    <img src={url} alt={`foto ${i + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Descripción (si no hay datos de visita) */}
          {(orden.descripcion || orden.comments) && !n.observaciones && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Descripción</p>
              <p className="text-sm text-gray-700 leading-relaxed">{orden.descripcion || orden.comments}</p>
            </div>
          )}

          {/* Notas internas */}
          {orden.comentariosPrivados && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Notas internas</p>
              <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2.5 leading-relaxed">{orden.comentariosPrivados}</p>
            </div>
          )}

          </>)}

          {/* ──── TAB GESTIÓN ──── */}
          {tab === 'gestion' && (<>

          {/* ── Acción de la oficina ── */}
          {grupo && !['cerrada', 'cancelada'].includes(orden.estado) && (
            <div className={`border rounded-xl p-4 ${grupo.headerCls}`}>
              <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${grupo.labelCls}`}>{grupo.label}</p>
              <p className="text-xs text-gray-600 mb-3 leading-snug">{grupo.desc}</p>
              {orden.cobrarVisita && (
                <div className="mb-3 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-xs text-purple-800 font-semibold">
                  📋 Hay que elaborar presupuesto — el técnico cobró la visita y anotó trabajo a presupuestar
                </div>
              )}
              {msg && (
                <p className={`text-xs mb-2 font-semibold ${msg.startsWith('Error') ? 'text-red-600' : 'text-emerald-700'}`}>{msg}</p>
              )}
              <button onClick={handleAccion} disabled={saving}
                className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${grupo.btnCls}`}>
                {saving ? <Spinner sm /> : grupo.btnLabel}
              </button>
            </div>
          )}

          {/* Factura vinculada — visible en cualquier estado */}
          {orden.facturaRef && !['cerrada', 'cancelada'].includes(orden.estado) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
              <p className="text-xs font-bold text-emerald-700">
                🧾 Factura {orden.facturaRef}{orden.fechaFactura ? ` · ${orden.fechaFactura}` : ''}
              </p>
              <p className="text-[10px] text-emerald-600 mt-0.5">Vinculada desde StelOrder — la orden se cerrará al sincronizar</p>
            </div>
          )}

          {['cerrada', 'cancelada'].includes(orden.estado) && (
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-center space-y-1">
              <p className="text-xs text-gray-500 font-semibold">
                {orden.estado === 'cerrada' ? '✓ Orden cerrada' : '✗ Orden cancelada'}
                {orden.facturada && !orden.facturaRef && ' · Facturada'}
                {(orden.gestionadaAriston || orden.gestionadaFabricante) && ' · Gestionada con fabricante'}
              </p>
              {orden.facturaRef && (
                <p className="text-xs font-bold text-emerald-700">
                  🧾 Factura {orden.facturaRef}{orden.fechaFactura ? ` · ${orden.fechaFactura}` : ''}
                </p>
              )}
            </div>
          )}

          {/* ── Seguimiento de garantía con fabricante ── */}
          {(orden.estado?.startsWith('garantia_') || orden.estado === 'pendiente_sustitucion') && (
            <div className="border border-teal-100 rounded-xl overflow-hidden">
              <div className="bg-teal-50 px-4 py-2.5 border-b border-teal-100">
                <p className="text-[10px] font-bold text-teal-700 uppercase tracking-wider">Seguimiento con fabricante</p>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Nº caso fabricante</label>
                  <input
                    value={garantiaForm.numeroCasoFabricante}
                    onChange={e => setGarantiaForm(p => ({ ...p, numeroCasoFabricante: e.target.value }))}
                    placeholder="Ej: AR-2025-00123"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Estado de la gestión</label>
                  <select
                    value={garantiaForm.estadoGarantia}
                    onChange={e => setGarantiaForm(p => ({ ...p, estadoGarantia: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                  >
                    <option value="">Sin actualizar</option>
                    <option value="enviada">Enviada al fabricante</option>
                    <option value="aprobada">Aprobada por fabricante</option>
                    <option value="rechazada">Rechazada por fabricante</option>
                    <option value="sustitucion_solicitada">Sustitución solicitada</option>
                    <option value="sustitucion_aprobada">Sustitución aprobada</option>
                    <option value="resuelta">Resuelta</option>
                  </select>
                </div>
                <button
                  onClick={handleGuardarGarantia}
                  disabled={savingGarantia}
                  className="w-full py-2 text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50"
                >
                  {savingGarantia ? 'Guardando...' : 'Guardar seguimiento'}
                </button>
                {garantiaError && (
                  <p className="text-xs text-red-600 font-semibold mt-1">{garantiaError}</p>
                )}
              </div>
            </div>
          )}

          </>)}

          {/* ──── TAB FIRMA ──── */}
          {tab === 'firma' && <FirmaTab orden={orden} n={n} />}

          {/* ──── TAB ADJUNTOS ──── */}
          {tab === 'adjuntos' && (
            <AdjuntosTab orden={orden} onOrdenChange={(updated) => { setOrden(updated); onUpdate(updated); }} />
          )}

          {/* ──── TAB RELACIONADOS ──── */}
          {/* El historial se sigue refrescando desde handleAccion (dentro de
              Gestión) — esto solo mueve DÓNDE se muestra, no cuándo se
              actualiza; `historial` es estado del panel, no de la pestaña. */}
          {tab === 'relacionados' && (
            <RelacionadosTab orden={orden} historial={historial} />
          )}

          {/* ──── TAB VISITAS ──── */}
          {tab === 'visitas' && (
            <VisitasPanel
              orden={orden}
              tecnicos={tecnicos}
              onVisitaAdded={(v) => {
                if (v.estado) {
                  const updated = { ...orden, estado: v.estado, updatedAt: new Date().toISOString().split('T')[0] };
                  setOrden(updated);
                  onUpdate(updated);
                }
              }}
            />
          )}

          {/* ──── TAB LÍNEAS ──── */}
          {tab === 'lineas' && (
            <LineasEditorPanel
              orden={orden}
              onDone={handleLinesDone}
              onCancel={() => setTab('mas_info')}
            />
          )}

        </div>
      </div>
  );
}

// ── EnCampoTab ─────────────────────────────────────────────────────────────────

function EnCampoTab({ ordenes, onDetalle, tecnicos }) {
  const listaTecnicos = tecnicos || TECNICOS_FALLBACK;

  const porTecnico = useMemo(() => {
    const map = { 'Sin asignar': [] };
    listaTecnicos.forEach(t => { map[t] = []; });
    ordenes.forEach(o => {
      const t = o.tecnicoNombre || 'Sin asignar';
      if (!map[t]) map[t] = [];
      map[t].push(o);
    });
    return map;
  }, [ordenes, listaTecnicos]);

  // Solo mostrar técnicos que tienen órdenes hoy
  const tecnicosConOrdenes = useMemo(() => {
    const set = new Set();
    ordenes.forEach(o => { if (o.tecnicoNombre) set.add(o.tecnicoNombre); });
    if (ordenes.some(o => !o.tecnicoNombre)) set.add('Sin asignar');
    return [...set];
  }, [ordenes]);

  const hayOrdenes = ordenes.length > 0;

  if (!hayOrdenes) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="font-bold text-gray-700 text-lg">Sin actividad hoy</p>
        <p className="text-sm text-gray-400 mt-1">No hay órdenes activas programadas para hoy</p>
      </div>
    );
  }

  return (
    // Columnas por técnico en fila, con scroll horizontal (no vertical) al
    // apilarse — pedido explícito del usuario, mismo patrón que
    // KanbanTodas/KanbanColumn más abajo en este fichero.
    <div className="flex gap-4 overflow-x-auto pb-2 items-start">
      {tecnicosConOrdenes.map(tecnico => {
        const items   = porTecnico[tecnico] || [];
        const dotCls  = TECNICO_DOT[tecnico]   || 'bg-gray-400';
        const badgeCls = TECNICO_BADGE[tecnico] || 'bg-gray-100 text-gray-700';
        return (
          <div key={tecnico} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm flex-shrink-0 w-72">
            <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${dotCls}`} />
              <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${badgeCls}`}>{tecnico}</span>
              <span className="text-xs text-gray-400 ml-auto">{items.length} orden{items.length !== 1 ? 'es' : ''}</span>
            </div>
            <div className="p-3 space-y-2">
              {items.map(o => {
                const estadoCfg = ESTADO_CFG[o.estado] || { label: o.estado, cls: 'bg-gray-100 text-gray-600' };
                const tipoCfg   = TIPO_CFG[o.tipo]     || { label: o.tipo || '—', cls: 'bg-gray-100 text-gray-700' };
                const n = norm(o);
                return (
                  <div key={o.id} onClick={() => onDetalle(o)}
                    className="bg-gray-50 rounded-xl p-2.5 cursor-pointer hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${estadoCfg.cls}`}>{estadoCfg.label}</span>
                      {n.referencia && <span className="text-[10px] font-mono text-gray-400">{n.referencia}</span>}
                    </div>
                    <p className="text-xs font-bold text-gray-800 leading-tight">{n.clienteNombre || '—'}</p>
                    {n.direccion && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{n.direccion}</p>}
                    {o.horaInicio && <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">{o.horaInicio}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── KanbanTodas ────────────────────────────────────────────────────────────────

const KANBAN_ORDER = [
  'pendiente', 'en_ruta', 'garantia', 'instalacion',
  'cobrada', 'cobrada_sin_firma', 'pendiente_pago', 'pendiente_material',
  'pendiente_presupuesto', 'pendiente_sustitucion', 'ausente',
  'garantia_ariston', 'garantia_nofer', 'garantia_svan', 'garantia_logman', 'garantia_beretta',
  'cerrada', 'cancelada',
];

// Mapeo de colores de cabecera de columna por estado
const KANBAN_HEADER = {
  pendiente:              'bg-yellow-100 text-yellow-800',
  en_ruta:                'bg-blue-100 text-blue-800',
  garantia:               'bg-indigo-100 text-indigo-800',
  instalacion:            'bg-indigo-100 text-indigo-700',
  cobrada:                'bg-emerald-100 text-emerald-800',
  cobrada_sin_firma:      'bg-yellow-100 text-yellow-800',
  pendiente_pago:         'bg-red-100 text-red-800',
  pendiente_material:     'bg-orange-100 text-orange-800',
  pendiente_presupuesto:  'bg-purple-100 text-purple-800',
  pendiente_sustitucion:  'bg-cyan-100 text-cyan-800',
  ausente:                'bg-yellow-100 text-yellow-700',
  garantia_ariston:       'bg-gray-100 text-gray-700',
  garantia_nofer:         'bg-gray-100 text-gray-700',
  garantia_svan:          'bg-gray-100 text-gray-700',
  garantia_logman:        'bg-gray-100 text-gray-700',
  garantia_beretta:       'bg-gray-100 text-gray-700',
  cerrada:                'bg-gray-100 text-gray-500',
  cancelada:              'bg-gray-100 text-gray-400',
};

function KanbanCardTodas({ orden, onDetalle }) {
  const n       = norm(orden);
  const tipoCfg = TIPO_CFG[orden.tipo] || { label: orden.tipo || '—', cls: 'bg-gray-100 text-gray-700' };
  const dotCls  = TECNICO_DOT[orden.tecnicoNombre] || 'bg-gray-400';

  return (
    <div onClick={() => onDetalle(orden)}
      className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:shadow-md cursor-pointer transition-shadow space-y-2">
      <div className="flex items-start justify-between gap-1">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tipoCfg.cls}`}>{tipoCfg.label}</span>
        <div className="flex items-center gap-1">
          {orden.prioridad === 'urgente' && (
            <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1 rounded">URG</span>
          )}
          {orden.ariston && (
            <span className="text-[10px] font-bold text-orange-700 bg-orange-50 px-1 rounded">Ariston</span>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-gray-900 leading-tight">{n.clienteNombre || '—'}</p>
        {n.direccion && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{n.direccion}</p>}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {orden.tecnicoNombre && (
            <div className={`w-5 h-5 rounded-full ${dotCls} text-white flex items-center justify-center text-[9px] font-bold`}>
              {orden.tecnicoNombre.charAt(0)}
            </div>
          )}
          {n.firmado && (
            <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center text-[9px] font-bold text-emerald-700">✓</div>
          )}
          {n.total !== null && n.total > 0 && (
            <span className="text-xs font-bold text-emerald-700">{n.total.toFixed(0)}€</span>
          )}
        </div>
        <div className="text-right">
          {n.referencia && <p className="text-[10px] text-gray-400 font-mono">{n.referencia}</p>}
          {orden.fechaProgramada && <p className="text-[10px] text-gray-400">{orden.fechaProgramada}</p>}
        </div>
      </div>
    </div>
  );
}

// Paginacion/listaPaginas → src/components/common/Paginacion.js (extraído
// para que Almacén y futuras tablas paginadas al estilo StelOrder lo reutilicen).

const KANBAN_PAGE_SIZE = 10; // tarjetas por página, por columna

function KanbanColumn({ estado, items, onDetalle }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [items]);

  const cfg    = ESTADO_CFG[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600' };
  const hdrCls = KANBAN_HEADER[estado] || 'bg-gray-100 text-gray-600';
  const totalPages = Math.max(1, Math.ceil(items.length / KANBAN_PAGE_SIZE));
  const pageItems = items.slice((page - 1) * KANBAN_PAGE_SIZE, page * KANBAN_PAGE_SIZE);

  return (
    <div className="flex-shrink-0 w-60">
      <div className={`flex items-center justify-between px-3 py-2 rounded-xl mb-2 ${hdrCls}`}>
        <p className="text-xs font-bold truncate">{cfg.label}</p>
        <span className="text-xs font-black bg-white/60 px-1.5 rounded-full flex-shrink-0 ml-1">{items.length}</span>
      </div>
      <div className="space-y-2">
        {pageItems.map(o => (
          <KanbanCardTodas key={o.id} orden={o} onDetalle={onDetalle} />
        ))}
      </div>
      <Paginacion page={page} totalPages={totalPages} onChange={setPage} compact />
    </div>
  );
}

function KanbanTodas({ ordenes, onDetalle }) {
  const columnas = useMemo(() => {
    const map = {};
    ordenes.forEach(o => {
      const e = o.estado || 'pendiente';
      if (!map[e]) map[e] = [];
      map[e].push(o);
    });
    return KANBAN_ORDER
      .filter(e => map[e]?.length > 0)
      .map(e => ({
        estado: e,
        items: map[e].slice().sort((a, b) => {
          const fa = a.fechaProgramada || '';
          const fb = b.fechaProgramada || '';
          return fa < fb ? -1 : fa > fb ? 1 : 0;
        }),
      }));
  }, [ordenes]);

  if (columnas.length === 0) {
    return <div className="flex justify-center py-16 text-sm text-gray-400">Sin órdenes con estos filtros</div>;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {columnas.map(({ estado, items }) => (
        <KanbanColumn key={estado} estado={estado} items={items} onDetalle={onDetalle} />
      ))}
    </div>
  );
}

// ── TodasTab ───────────────────────────────────────────────────────────────────

const PAGE_SIZE_TODAS = 25; // estilo StelOrder — 25 filas por página, paginado con números

// Etiqueta de cabecera ordenable (Fecha/Estado) — flecha visible solo en la
// columna activa. Va DENTRO del <th>, no lo sustituye, para poder compartir
// las mismas clases de padding que el resto de cabeceras.
function TituloOrdenable({ label, campo, sort, onSort }) {
  const activo = sort.campo === campo;
  return (
    <button onClick={() => onSort(campo)} className="flex items-center gap-1 hover:text-gray-800">
      {label}
      <span className={`text-[9px] ${activo ? 'text-gray-700' : 'text-gray-300'}`}>
        {activo && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </button>
  );
}

function TodasTab({ ordenes, onDetalle }) {
  // Búsqueda por columna al estilo StelOrder — complementa (no sustituye) el
  // buscador global y los desplegables de arriba, que siguen aplicándose
  // antes de llegar aquí vía `ordenes`.
  const [colFiltros, setColFiltros] = useState({ referencia: '', cliente: '', tecnico: '' });
  const [sort, setSort] = useState({ campo: null, dir: 'asc' });
  const [page, setPage] = useState(1);

  const toggleSort = (campo) => {
    setSort(s => (s.campo === campo ? { campo, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }));
  };

  const filtradas = useMemo(() => {
    const rf = colFiltros.referencia.trim().toLowerCase();
    const cf = colFiltros.cliente.trim().toLowerCase();
    const tf = colFiltros.tecnico.trim().toLowerCase();
    let arr = ordenes.filter(o => {
      const n = norm(o);
      if (rf && !n.referencia.toLowerCase().includes(rf)) return false;
      if (cf && !n.clienteNombre.toLowerCase().includes(cf)) return false;
      if (tf && !(o.tecnicoNombre || '').toLowerCase().includes(tf)) return false;
      return true;
    });
    if (sort.campo) {
      arr = [...arr].sort((a, b) => {
        const av = (sort.campo === 'fecha' ? a.fechaProgramada : a.estado) || '';
        const bv = (sort.campo === 'fecha' ? b.fechaProgramada : b.estado) || '';
        return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return arr;
  }, [ordenes, colFiltros, sort]);

  useEffect(() => { setPage(1); }, [ordenes, colFiltros, sort]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / PAGE_SIZE_TODAS));
  const pageClamped = Math.min(page, totalPages);
  const visiblesArr = filtradas.slice((pageClamped - 1) * PAGE_SIZE_TODAS, pageClamped * PAGE_SIZE_TODAS);

  const colInp = 'w-full border border-gray-200 rounded-lg px-2 py-1 text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-red-400';

  if (ordenes.length === 0) {
    return <div className="flex justify-center py-16 text-sm text-gray-400">Sin órdenes con estos filtros</div>;
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden h-full flex flex-col">
      <div className="overflow-auto flex-1 min-h-0">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Ref.</th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Cliente</th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Técnico</th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell select-none">
              <TituloOrdenable label="Fecha" campo="fecha" sort={sort} onSort={toggleSort} />
            </th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider select-none">
              <TituloOrdenable label="Estado" campo="estado" sort={sort} onSort={toggleSort} />
            </th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Tipo</th>
            <th className="px-4 pt-3 pb-1 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Firma / €</th>
            <th className="px-4 py-3" />
          </tr>
          <tr>
            <th className="px-4 pb-2"><input value={colFiltros.referencia} onChange={e => setColFiltros(f => ({ ...f, referencia: e.target.value }))} placeholder="Buscar…" className={colInp} /></th>
            <th className="px-4 pb-2"><input value={colFiltros.cliente} onChange={e => setColFiltros(f => ({ ...f, cliente: e.target.value }))} placeholder="Buscar…" className={colInp} /></th>
            <th className="px-4 pb-2 hidden md:table-cell"><input value={colFiltros.tecnico} onChange={e => setColFiltros(f => ({ ...f, tecnico: e.target.value }))} placeholder="Buscar…" className={colInp} /></th>
            <th className="px-4 pb-2 hidden lg:table-cell" />
            <th className="px-4 pb-2" />
            <th className="px-4 pb-2 hidden sm:table-cell" />
            <th className="px-4 pb-2 hidden lg:table-cell" />
            <th className="px-4 pb-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {filtradas.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">Sin resultados para esos filtros de columna</td></tr>
          )}
          {visiblesArr.map(o => {
            const tipoCfg   = TIPO_CFG[o.tipo]     || { label: o.tipo || '—',   cls: 'bg-gray-100 text-gray-700' };
            const estadoCfg = ESTADO_CFG[o.estado] || { label: o.estado || '—', cls: 'bg-gray-100 text-gray-600' };
            const n = norm(o);
            return (
              <tr key={o.id} onClick={() => onDetalle(o)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <p className="font-mono text-xs font-bold text-gray-700">{n.referencia || '—'}</p>
                    {o.prioridad === 'urgente' && (
                      <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1 py-0.5 rounded">URG</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900 truncate max-w-[150px]">{n.clienteNombre || '—'}</p>
                  {n.direccion && <p className="text-xs text-gray-400 truncate max-w-[150px]">{n.direccion}</p>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  {o.tecnicoNombre
                    ? <TecnicoBadge nombre={o.tecnicoNombre} />
                    : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">
                  {o.fechaProgramada || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${estadoCfg.cls}`}>{estadoCfg.label}</span>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tipoCfg.cls}`}>{tipoCfg.label}</span>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${n.firmado ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      {n.firmado ? '✓' : '—'}
                    </div>
                    {n.total !== null && n.total > 0 && (
                      <span className="text-xs text-gray-600 font-mono">{n.total.toFixed(0)}€</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {filtradas.length > 0 && (
        <div className="flex items-center justify-between px-4 border-t border-gray-50 flex-shrink-0">
          <span className="text-[11px] text-gray-400">
            {(pageClamped - 1) * PAGE_SIZE_TODAS + 1}–{Math.min(pageClamped * PAGE_SIZE_TODAS, filtradas.length)} de {filtradas.length}
          </span>
          <Paginacion page={pageClamped} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

// ── FacturarModal ──────────────────────────────────────────────────────────────

function FacturarModal({ orden, onClose, onDone }) {
  const n = norm(orden);
  const hoy = new Date().toISOString().split('T')[0];
  const lineasTotal = (orden.lineas || []).reduce((s, l) => s + Number(l.cantidad || 1) * Number(l.precioUnitario || 0), 0);

  const [form, setForm] = useState({
    fecha:   orden.fechaProgramada || hoy,
    tipoIva: 21,
    importe: lineasTotal > 0 ? lineasTotal.toFixed(2) : '',
    notas:   '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const pct   = Number(form.tipoIva) / 100;
  const base  = parseFloat(form.importe) || 0;
  const iva   = parseFloat((base * pct).toFixed(2));
  const total = parseFloat((base + iva).toFixed(2));

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';

  const handleSave = async () => {
    if (!base) { setErr('Introduce el importe base (sin IVA)'); return; }
    setSaving(true); setErr('');
    try {
      const res = await fetch('/.netlify/functions/saveFactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          fecha:         form.fecha,
          tipoIva:       Number(form.tipoIva),
          importe:       base,
          importeIva:    iva,
          importeTotal:  total,
          clienteNombre: n.clienteNombre || '',
          ordenId:       orden.id,
          ordenRef:      n.referencia,
          notas:         form.notas || null,
          metodoPago:    String(orden['payment-option-id'] || orden.formaPago || ''),
          lineas:        orden.lineas || [],
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error creando factura');
      await onDone();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900 text-base">Generar factura</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-0.5">
            <p className="font-bold text-gray-800">{n.clienteNombre || '—'}</p>
            <p className="text-gray-500">{n.referencia} · {orden.fechaProgramada || '—'}</p>
          </div>

          {err && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Fecha factura</label>
              <input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} className={inp} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Tipo IVA</label>
              <select value={form.tipoIva} onChange={e => set('tipoIva', e.target.value)} className={inp}>
                <option value={21}>21% General</option>
                <option value={10}>10% Reducido</option>
                <option value={0}>0% Exento</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Base imponible (€)</label>
            <input
              type="number" min="0" step="0.01"
              value={form.importe}
              onChange={e => set('importe', e.target.value)}
              placeholder="0.00"
              className={inp}
            />
          </div>

          {base > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between text-gray-600">
                <span>Base</span><span>{base.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>IVA ({form.tipoIva}%)</span><span>{iva.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between font-bold text-emerald-800 text-base pt-1 border-t border-emerald-200 mt-1">
                <span>Total</span><span>{total.toFixed(2)} €</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Notas (opcional)</label>
            <textarea
              value={form.notas}
              onChange={e => set('notas', e.target.value)}
              rows={2}
              className={inp + ' resize-none'}
              placeholder="Observaciones..."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t flex gap-3">
          <button onClick={onClose} disabled={saving}
            className="flex-1 py-2 text-sm font-bold border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || !base}
            className="flex-1 py-2 text-sm font-bold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50">
            {saving ? '...' : '📄 Generar y cerrar orden'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ────────────────────────────────────────────────────────────

export default function OrdenesView({ initialTab, onNavigate } = {}) {
  const tecnicos = useTecnicos();

  const [tab,           setTab]           = useState(initialTab || 'todas');
  const [ordenes,       setOrdenes]       = useState([]);
  const [ordenesHoy,    setOrdenesHoy]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [seleccionada,  setSeleccionada]  = useState(null);
  const [reprogramar,   setReprogramar]   = useState(null);
  const [savingId,      setSavingId]      = useState(null);
  const [facturarOrden, setFacturarOrden] = useState(null);
  const [error,         setError]         = useState('');
  // fecha: 'hoy' por defecto (estilo StelOrder) — precarga solo el día en
  // vez de toda la colección; ampliar a 'semana' o '' (todas) es una
  // decisión explícita del usuario, no el estado inicial.
  const [filtros,       setFiltros]       = useState({ tecnico: '', tipo: '', estado: '', fecha: 'hoy', fechaDesde: '', fechaHasta: '' });
  const [vista,         setVista]         = useState('lista');
  const [busqueda,      setBusqueda]      = useState('');
  const [lastDocRaw,    setLastDocRaw]    = useState(null);
  const [lastDocOld,    setLastDocOld]    = useState(null);
  const [hasMore,       setHasMore]       = useState(false);
  const [loadingMore,   setLoadingMore]   = useState(false);

  // PAGE_SIN_RANGO solo se usa en modo "Todas las fechas" (sin acotar) — al
  // haber un rango de fecha activo (hoy/semana) el propio rango ya acota el
  // resultado, así que no hace falta paginar la lectura de Firestore.
  const PAGE_SIN_RANGO = 100;

  const cargarOrdenes = useCallback(async (cursorRaw = null, cursorOld = null) => {
    // "Seleccionar rango" sin las dos fechas puestas todavía no es un
    // rango válido ni "Todas las fechas" — no hay nada que cargar aún.
    if (filtros.fecha === 'rango' && (!filtros.fechaDesde || !filtros.fechaHasta)) return;
    const append = cursorRaw !== null || cursorOld !== null;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const rango = rangoFechas(filtros.fecha, filtros.fechaDesde, filtros.fechaHasta);
      let snapRaw, snapOld;
      if (rango) {
        // Rango acotado: una sola lectura por variante de esquema, sin
        // cursor — al cambiar de rango se reemplaza la lista entera, nunca
        // se acumula. Esto es lo que hace que "Hoy" precargue solo el día.
        const q1 = query(collection(db, 'ordenes'), where('date', '>=', rango.desde), where('date', '<', rango.hasta), limit(1000));
        const q2 = query(collection(db, 'ordenes'), where('fechaProgramada', '>=', rango.desde), where('fechaProgramada', '<', rango.hasta), limit(1000));
        [snapRaw, snapOld] = await Promise.all([getDocs(q1), getDocs(q2)]);
        setHasMore(false);
        setLastDocRaw(null); setLastDocOld(null);
      } else {
        // "Todas las fechas" — sin acotar por fecha, así que aquí sí hace
        // falta paginar de verdad: PAGE_SIN_RANGO documentos por página, y
        // SOLO al pulsar "Cargar más" (ver más abajo) — nunca automático,
        // para no acabar trayéndose la colección entera de golpe.
        const baseRaw = [collection(db, 'ordenes'), orderBy('date', 'desc'), limit(PAGE_SIN_RANGO)];
        const baseOld = [collection(db, 'ordenes'), orderBy('fechaProgramada', 'desc'), limit(PAGE_SIN_RANGO)];
        const q1 = cursorRaw ? query(...baseRaw, startAfter(cursorRaw)) : query(...baseRaw);
        const q2 = cursorOld ? query(...baseOld, startAfter(cursorOld)) : query(...baseOld);
        [snapRaw, snapOld] = await Promise.all([getDocs(q1), getDocs(q2)]);
        setLastDocRaw(snapRaw.docs[snapRaw.docs.length - 1] || cursorRaw);
        setLastDocOld(snapOld.docs[snapOld.docs.length - 1] || cursorOld);
        setHasMore(snapRaw.docs.length === PAGE_SIN_RANGO || snapOld.docs.length === PAGE_SIN_RANGO);
      }
      const batch = [
        ...snapRaw.docs.map(d => ({ id: d.id, ...d.data() })),
        ...snapOld.docs.map(d => ({ id: d.id, ...d.data() })),
      ].map(normalizeRaw);
      setOrdenes(prev => {
        const base = append ? prev : [];
        const merged = mergeDuplicados([...base, ...batch])
          .sort((a, b) => (b.fechaProgramada || '').localeCompare(a.fechaProgramada || ''));
        // En segundo plano, sin bloquear la lista inicial: (1) si hay rango
        // acotado, descarta/corrige los "ord-" sueltos cuya fecha real (la
        // del gemelo numérico, si existe) ya no cae en ese rango — evita
        // que una orden reprogramada después de su última foto congelada
        // siga apareciendo en "Hoy"/"Esta semana"; (2) rellena nombre/
        // dirección de cliente para las que lleguen sin él.
        const pipeline = rango
          ? reconciliarViejos(merged, f => f >= rango.desde && f < rango.hasta).then(enriquecerConClientes)
          : enriquecerConClientes(merged);
        const mergedIds = new Set(merged.map(o => o.id));
        pipeline.then(resultado => {
          const porId = new Map(resultado.map(o => [o.id, o]));
          setOrdenes(curr => curr
            .filter(o => !mergedIds.has(o.id) || porId.has(o.id)) // quita del lote actual lo que reconciliarViejos descartó
            .map(o => porId.get(o.id) || o));
        });
        return merged;
      });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [filtros.fecha, filtros.fechaDesde, filtros.fechaHasta]); // eslint-disable-line

  // Recarga desde cero cada vez que cambia el rango de fecha — no se
  // acumula sobre lo anterior (p. ej. al pasar de "Hoy" a "Todas" no hay
  // que arrastrar el día ya cargado, se sustituye entero).
  useEffect(() => {
    if (filtros.fecha === 'rango' && (!filtros.fechaDesde || !filtros.fechaHasta)) return;
    setOrdenes([]);
    setLastDocRaw(null);
    setLastDocOld(null);
    setHasMore(false);
    cargarOrdenes();
  }, [filtros.fecha, filtros.fechaDesde, filtros.fechaHasta]); // eslint-disable-line

  // Listener hoy — dual-query. Rango raw ampliado ±1 día para absorber desfases UTC
  useEffect(() => {
    const today    = hoyMadrid();
    const ayer     = sumarDias(today, -1);
    const pasado   = sumarDias(today, 2);
    let dataOld = [], dataNew = [];
    let gen = 0; // descarta un enriquecimiento en vuelo si ya llegó un merge más nuevo
    const merge = () => {
      const miGen = ++gen;
      const all = mergeDuplicados([...dataOld, ...dataNew].map(normalizeRaw))
        .filter(o => o.fechaProgramada === today); // garantizar que son de hoy
      // Antes también exigía o.clienteNombre truthy — pero las órdenes raw
      // recién sincronizadas de StelOrder no traen nombre de cliente en el
      // propio documento (solo account-id/account-path). Se muestran ya
      // (sin bloquear en la promesa) y se enriquecen con el nombre real en
      // cuanto resuelve la consulta a "clientes" — mismo patrón que
      // getStelOrderData.js (WeeklyAgenda).
      const final = all.filter(o => ESTADOS_CAMPO_HOY.has(o.estado));
      setOrdenesHoy(final);
      reconciliarViejos(final, f => f === today)
        .then(reconciliadas => enriquecerConClientes(reconciliadas.filter(o => ESTADOS_CAMPO_HOY.has(o.estado))))
        .then(enriquecidas => { if (miGen === gen) setOrdenesHoy(enriquecidas); });
    };
    const qOld = query(collection(db, 'ordenes'), where('fechaProgramada', '==', today));
    const qNew = query(collection(db, 'ordenes'), where('date', '>=', ayer), where('date', '<', pasado), limit(300));
    const u1 = onSnapshot(qOld, snap => { dataOld = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); }, () => {});
    const u2 = onSnapshot(qNew, snap => { dataNew = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); }, () => {});
    return () => { u1(); u2(); };
  }, []);

  // Ya no hay botón de sync manual en esta vista: el cron syncDeltaStelOrder
  // (cada 30 min en horario laboral) mantiene Firebase al día solo, y todas
  // las lecturas de aquí son siempre contra Firestore — no hace falta que
  // el usuario dispare ni vigile la sincronización con StelOrder.

  // Búsqueda textual aplicada a "En campo hoy"
  const ordenesHoyFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return ordenesHoy;
    return ordenesHoy.filter(o => {
      const n = norm(o);
      return (
        n.clienteNombre.toLowerCase().includes(q) ||
        n.referencia.toLowerCase().includes(q) ||
        n.clientePhone.toLowerCase().includes(q)
      );
    });
  }, [ordenesHoy, busqueda]);

  // Filtrado client-side para tab "Todas" — el filtro de fecha ya se aplica
  // al leer de Firestore (cargarOrdenes), así que aquí solo quedan los
  // filtros que no cambian qué se lee, solo qué se muestra de lo ya leído.
  const ordenesFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return ordenes.filter(o => {
      if (filtros.tecnico && (o.tecnicoNombre || '') !== filtros.tecnico)    return false;
      if (filtros.tipo    && (o.tipo || '') !== filtros.tipo)                return false;
      if (filtros.estado  && (o.estado || '') !== filtros.estado)            return false;
      if (q) {
        const n = norm(o);
        if (!n.clienteNombre.toLowerCase().includes(q) &&
            !n.referencia.toLowerCase().includes(q) &&
            !n.clientePhone.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [ordenes, filtros, busqueda]);

  // Lista para el panel compacto izquierdo cuando hay orden seleccionada
  const listaActual = useMemo(() => {
    if (tab === 'campo') return ordenesHoyFiltradas;
    return ordenesFiltradas;
  }, [tab, ordenesHoyFiltradas, ordenesFiltradas]);

  const metrics = useMemo(() => ({
    enCampo: ordenesHoy.length,
  }), [ordenesHoy]);

  const handleCancelar = async (orden) => {
    setSavingId(orden.id);
    try {
      await patchOrden(orden, { estado: 'cancelada', notasHistorial: 'Cancelada desde gestión' });
      if (seleccionada?.id === orden.id) setSeleccionada(null);
    } catch (e) { setError(e.message); }
    finally { setSavingId(null); }
  };

  const handleUpdate = (updated) => {
    setSeleccionada(updated);
    setOrdenes(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
  };

  const handleReprogramado = (updated) => {
    if (seleccionada?.id === updated.id) setSeleccionada(updated);
    setReprogramar(null);
  };

  const fCls = 'border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';

  const TABS = [
    { key: 'campo',       label: 'En campo hoy',  count: metrics.enCampo,      alert: false },
    { key: 'todas',       label: 'Órdenes',        count: null,                 alert: false },
    { key: 'incidencias', label: 'Incidencias',    count: null,                 alert: false },
    { key: 'agenda',      label: 'Agenda semanal', count: null,                 alert: false },
    { key: 'rutas',       label: 'Rutas',          count: null,                 alert: false },
    { key: 'comisiones',  label: 'Comisiones',     count: null,                 alert: false },
    { key: 'analitica',   label: 'Analítica',      count: null,                 alert: false },
  ];
  // "Agenda semanal", "Rutas", "Comisiones", "Analítica" e "Incidencias"
  // traen su propia interfaz completa (calendario / cierres de ruta /
  // comisiones / dashboard / lista+detalle autónomos) — no aplican el
  // buscador, los filtros de "Órdenes" ni el botón "+ Nueva orden",
  // pensados para una lista de órdenes.
  const esVistaEspecial = tab === 'agenda' || tab === 'analitica' || tab === 'incidencias' || tab === 'rutas' || tab === 'comisiones';
  // De las anteriores, estas tres no traen su propio scroll interno — son
  // informes largos (tarjetas colapsables, tablas de historial) pensados
  // para crecer y hacer scroll de página completa, no listados acotados a
  // la altura fija de la pantalla.
  const esVistaLarga = tab === 'analitica' || tab === 'rutas' || tab === 'comisiones';

  return (
    <>
      {reprogramar && (
        <ReprogramarModal
          tecnicos={tecnicos}
          orden={reprogramar}
          onClose={() => setReprogramar(null)}
          onDone={handleReprogramado}
        />
      )}
      {facturarOrden && (
        <FacturarModal
          orden={facturarOrden}
          onClose={() => setFacturarOrden(null)}
          onDone={async () => {
            const orden = facturarOrden;
            setSavingId(orden.id);
            try {
              const patch = { estado: 'cerrada', facturada: true };
              await patchOrden(orden, patch);
              if (seleccionada?.id === orden.id) setSeleccionada(p => p ? { ...p, ...patch } : p);
              if (orden.stelorderId && /^\d+$/.test(String(orden.stelorderId))) {
                fetch('/.netlify/functions/updateWorkorderState', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
                  body: JSON.stringify({ workorderId: orden.stelorderId, newStateId: 651428 }),
                }).catch(() => {});
              }
            } catch (e) { setError(e.message); }
            finally { setSavingId(null); }
            setFacturarOrden(null);
          }}
        />
      )}
      {seleccionada ? (
        /* ── Vista detalle: lista compacta + panel ── */
        <div className="flex gap-4 h-[calc(100vh-160px)]">
          {/* Lista compacta izquierda */}
          <div className="w-80 flex-shrink-0 hidden md:flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setSeleccionada(null)}
                className="text-sm font-semibold text-gray-600 hover:text-gray-800 flex items-center gap-1">
                ← Lista
              </button>
              <span className="text-xs text-gray-400 ml-auto">
                {tab === 'campo' ? 'En campo hoy' : 'Todas'}
                {' '}· {listaActual.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {listaActual.map(o => {
                const isSelected = o.id === seleccionada?.id;
                const nO = norm(o);
                const eCfg = ESTADO_CFG[o.estado] || { label: o.estado || '—', cls: 'bg-gray-100 text-gray-500' };
                return (
                  <button key={o.id} onClick={() => setSeleccionada(o)}
                    className={`w-full text-left px-4 py-3 transition-colors ${isSelected ? 'bg-red-50 border-l-[3px] border-red-500' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${eCfg.cls}`}>{eCfg.label}</span>
                      {o.tecnicoNombre && <TecnicoBadge nombre={o.tecnicoNombre} />}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 truncate">{nO.clienteNombre || '—'}</p>
                    <p className="text-xs text-gray-400 truncate">{[nO.referencia, o.fechaProgramada].filter(Boolean).join(' · ')}</p>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Panel de detalle */}
          <OrdenDetallePanel
            orden={seleccionada}
            onClose={() => setSeleccionada(null)}
            onUpdate={handleUpdate}
            onReprogramar={setReprogramar}
            onCancelar={handleCancelar}
            onFacturar={setFacturarOrden}
          />
        </div>
      ) : (
      // UI fija al estilo StelOrder: cabecera/buscador/tabs/filtros no se
      // mueven — solo el listado de dentro hace scroll, en vez de la
      // página entera. Mismo presupuesto de alto que ya usaba la vista de
      // detalle (h-[calc(100vh-160px)]) para quedar consistentes. Analítica/
      // Rutas/Comisiones se quedan fuera a propósito (ver esVistaLarga).
      <div className={esVistaLarga ? '' : 'flex flex-col h-[calc(100vh-160px)]'}>

        {/* ── Tabs + toolbar ── */}
        <div className={`bg-white rounded-2xl border shadow-sm ${esVistaLarga ? '' : 'flex flex-col flex-1 min-h-0 overflow-hidden'}`}>

          {/* Buscador global — sin botón de sync manual: la lectura siempre
              es contra Firestore (tiempo real via onSnapshot donde aplica),
              y StelOrder→Firebase lo mantiene al día el cron en segundo
              plano, no una acción del usuario. No aplica a "Agenda semanal"
              (calendario propio, no una lista de órdenes que buscar). */}
          {!esVistaEspecial && (
            <div className="px-4 pt-3 pb-2 border-b flex-shrink-0">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  placeholder="Buscar por cliente, referencia o teléfono..."
                  className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 bg-gray-50"
                />
                {busqueda && (
                  <button onClick={() => setBusqueda('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm leading-none">
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Tabs — mismo diseño de píldoras que Mantenimientos (Contratos),
              para que el "menú" de secciones se vea igual en toda la app.
              overflow-x-auto es lo que lo hace responsive: en pantallas
              estrechas se desliza en horizontal en vez de partirse en varias
              líneas. */}
          <div className="px-4 pt-3 border-b flex-shrink-0 overflow-x-auto -mx-1">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-max mx-1 mb-3">
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap ${
                    tab === t.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {t.alert && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                  {t.label}
                  {t.count !== null && (
                    <span className={`text-xs font-black ${tab === t.key ? 'text-red-600' : 'text-gray-400'}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros/"Nueva orden" — nada de esto aplica a las pestañas
              autónomas (esVistaEspecial), así que la fila entera se omite
              para ellas en vez de dejar una tira vacía con borde. */}
          {!esVistaEspecial && (
          <div className="px-4 pt-3 flex flex-wrap items-end gap-2 border-b flex-shrink-0">
            {/* Filtros + toggle vista (solo en "Todas") */}
            {tab === 'todas' && (
              <div className="flex flex-wrap items-center gap-2 pb-2">
                {/* Toggle Lista / Kanban */}
                <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5">
                  {[['lista', '☰ Lista'], ['kanban', '⬛ Kanban']].map(([v, label]) => (
                    <button key={v} onClick={() => setVista(v)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${vista === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                      {label}
                    </button>
                  ))}
                </div>

                <select value={filtros.fecha} onChange={e => setFiltros(f => ({ ...f, fecha: e.target.value }))} className={fCls}>
                  <option value="hoy">Hoy</option>
                  <option value="semana">Esta semana</option>
                  <option value="mes">Este mes</option>
                  <option value="anio">Este año</option>
                  <option value="rango">Seleccionar rango…</option>
                  <option value="">Todas las fechas</option>
                </select>
                {filtros.fecha === 'rango' && (
                  <>
                    <input type="date" value={filtros.fechaDesde}
                      onChange={e => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))}
                      className={fCls} />
                    <span className="text-xs text-gray-400">→</span>
                    <input type="date" value={filtros.fechaHasta}
                      onChange={e => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))}
                      className={fCls} />
                  </>
                )}
                <select value={filtros.tecnico} onChange={e => setFiltros(f => ({ ...f, tecnico: e.target.value }))} className={fCls}>
                  <option value="">Todos los técnicos</option>
                  {tecnicos.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))} className={fCls}>
                  <option value="">Todos los tipos</option>
                  {Object.entries(TIPO_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))} className={fCls}>
                  <option value="">Todos los estados</option>
                  {Object.entries(ESTADO_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                {/* "hoy" es el valor por defecto, no cuenta como filtro activo
                    a efectos de mostrar "Limpiar" — evita que el botón
                    aparezca siempre con la carga inicial. */}
                {(filtros.fecha !== 'hoy' || filtros.tecnico || filtros.tipo || filtros.estado) && (
                  <button onClick={() => setFiltros({ tecnico: '', tipo: '', estado: '', fecha: 'hoy', fechaDesde: '', fechaHasta: '' })}
                    className="text-xs text-red-500 font-bold hover:text-red-700 whitespace-nowrap">
                    × Limpiar
                  </button>
                )}
                <span className="text-xs text-gray-400 whitespace-nowrap">{ordenesFiltradas.length} órdenes</span>
              </div>
            )}

            {/* Nueva orden — redirige al CRM en vez de un alta propia: el
                formulario que tenía aquí (saveOrden.js) creaba la orden SOLO
                en Firebase (stelorderId:null, numeración propia), sin equipo
                ni guión, y nunca llegaba a StelOrder — el técnico recibía
                menos información y la orden no se facturaba por el circuito
                normal. El del CRM sí crea la orden real en StelOrder. */}
            <button onClick={() => onNavigate?.('incidence')}
              className="mb-2 flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap">
              + Nueva orden
            </button>
          </div>
          )}

          {/* Contenido — el listado interior hace scroll, no la página
              (Analítica se deja crecer, ver arriba). "Órdenes" e
              "Incidencias" además fijan su paginador abajo: gestionan su
              propio scroll interno, así que aquí NO se aplica
              overflow-y-auto para ellas — serían dos scrolls anidados. */}
          <div className={
            (tab === 'analitica' || tab === 'comisiones') ? 'p-4'
            // RouteClosuresList ya trae su propio padding (p-4 md:p-6) —
            // envolverla en otro 'p-4' aquí duplicaría el aire alrededor.
            : tab === 'rutas' ? ''
            : (tab === 'todas' || tab === 'incidencias') ? 'p-4 flex-1 min-h-0 flex flex-col overflow-hidden'
            : 'p-4 flex-1 min-h-0 overflow-y-auto'
          }>
            {error && !esVistaEspecial && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm mb-4">{error}</div>
            )}
            {tab === 'agenda' ? (
              // WeeklyAgenda es autónoma — su propio fetch, loading y error,
              // no depende de `ordenes`/`loading` de OrdenesView.
              <WeeklyAgenda />
            ) : tab === 'rutas' ? (
              // Igual de autónoma — cierres de ruta en tiempo real
              // (onSnapshot propio), no depende de nada de esta vista.
              <RouteClosuresList />
            ) : tab === 'comisiones' ? (
              // Igual de autónoma — comisiones en tiempo real (onSnapshot
              // propio), no depende de nada de esta vista.
              <CommissionsList />
            ) : tab === 'incidencias' ? (
              // Igual de autónoma — lee la colección `incidencias` directo
              // (mismo patrón que "Órdenes" con `ordenes`), con su propio
              // buscador/filtro/detalle/paginador, no depende de nada de
              // esta vista. h-full para ocupar el flex-1 de arriba.
              <IncidenciasTab />
            ) : tab === 'analitica' ? (
              // Igual de autónoma — pide los KPIs ya agregados al backend
              // (getOperacionesKPIs.js), no depende del filtro de fecha
              // activo en "Todas".
              <OrdenesAnalitica
                onIrATodas={(estado) => { setTab('todas'); setFiltros(f => ({ ...f, estado, fecha: '' })); }}
                onAbrirOrden={(referencia) => { setTab('todas'); setFiltros({ tecnico: '', tipo: '', estado: '', fecha: '' }); setBusqueda(referencia); }}
              />
            ) : loading ? (
              <div className="flex justify-center py-16"><Spinner /></div>
            ) : tab === 'campo' ? (
              <div className="h-full overflow-y-auto">
                <EnCampoTab ordenes={ordenesHoyFiltradas} onDetalle={setSeleccionada} tecnicos={tecnicos} />
              </div>
            ) : vista === 'kanban' ? (
              <div className="h-full overflow-y-auto">
                <KanbanTodas ordenes={ordenesFiltradas} onDetalle={setSeleccionada} />
              </div>
            ) : (
              <div className="flex flex-col h-full min-h-0">
                {/* "Cargar más" solo existe en modo "Todas las fechas" (sin
                    acotar) — con un rango de fecha activo (Hoy/Semana) el
                    rango ya trae todo lo que hay, no hace falta paginar
                    contra Firestore. Nunca se dispara solo: sin esto, un
                    listado grande de "Todas las fechas" acababa trayéndose
                    la colección entera de golpe. */}
                {!filtros.fecha && (hasMore || loadingMore) && (
                  <div className="flex items-center justify-between gap-2 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-500 mb-2 flex-shrink-0">
                    <span>{ordenes.length} órdenes cargadas{hasMore ? ' — puede haber más' : ''}</span>
                    {hasMore && (
                      <button
                        onClick={() => cargarOrdenes(lastDocRaw, lastDocOld)}
                        disabled={loadingMore}
                        className="px-3 py-1 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        {loadingMore ? 'Cargando…' : 'Cargar más'}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex-1 min-h-0">
                  <TodasTab ordenes={ordenesFiltradas} onDetalle={setSeleccionada} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </>
  );
}

// Compartido con OrdenesAnalitica.js (pestaña "Analítica") — solo estilos
// de badge para pintar los mismos colores/etiquetas que el resto de la
// vista. El cálculo de KPIs en sí vive en getOperacionesKPIs.js (backend),
// no aquí — ver el comentario de cabecera de ese fichero.
export { ESTADO_CFG, TECNICO_BADGE, TECNICO_DOT, Paginacion };
